import { useEffect, useState, useRef, useCallback } from 'react';
import LineChart from '../components/LineChart';
import DonutChart from '../components/DonutChart';
import HorizontalBarChart from '../components/HorizontalBarChart';
import PeriodSelector from '../components/PeriodSelector';
import DataTable from '../components/DataTable';
import { usePeriod } from '../contexts/PeriodContext';
import { useUseCase } from '../contexts/UseCaseContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { logger } from '../utils/logger';
import { Upload, Download, HelpCircle, CheckCircle2, AlertTriangle, ExternalLink, ShieldCheck, Plus, XCircle, Trash2, Building2, Truck } from 'lucide-react';
import { createPortal } from 'react-dom';
import { redactVehicleId, redactVehicleType, redactStationName, redactTransportService, redactCityName } from '../utils/demoRedaction';
import EmissionFormulaTooltip from '../components/EmissionFormulaTooltip';
import ClientCarbonReporting from '../components/ClientCarbonReporting';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';
import jsPDF from 'jspdf';
import { authenticatedFetch } from '../utils/authenticatedFetch';

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

type CarbonReportingSection = 'fleet' | 'client';

interface CSVRow {
  [key: string]: string;
}

interface TimeSeriesData {
  emissions: {
    daily: Array<{ date: string; total_emissions_kg_co2e: number }>;
    weekly: Array<{ week_start: string; total_emissions_kg_co2e: number }>;
    monthly: Array<{ month: string; total_emissions_kg_co2e: number }>;
  };
  scope1_emissions?: {
    daily: Array<{ date: string; total_emissions_kg_co2e: number }>;
    weekly: Array<{ week_start: string; total_emissions_kg_co2e: number }>;
    monthly: Array<{ month: string; total_emissions_kg_co2e: number }>;
  };
  scope2_emissions?: {
    daily: Array<{ date: string; total_emissions_kg_co2e: number }>;
    weekly: Array<{ week_start: string; total_emissions_kg_co2e: number }>;
    monthly: Array<{ month: string; total_emissions_kg_co2e: number }>;
  };
  fuel_costs: {
    daily: Array<{ date: string; total_fuel_costs_gbp: number }>;
    weekly: Array<{ week_start: string; total_fuel_costs_gbp: number }>;
    monthly: Array<{ month: string; total_fuel_costs_gbp: number }>;
  };
  diesel_fuel_costs?: {
    daily: Array<{ date: string; diesel_fuel_costs_gbp: number }>;
    weekly: Array<{ week_start: string; diesel_fuel_costs_gbp: number }>;
    monthly: Array<{ month: string; diesel_fuel_costs_gbp: number }>;
  };
  electricity_costs?: {
    daily: Array<{ date: string; electricity_costs_gbp: number }>;
    weekly: Array<{ week_start: string; electricity_costs_gbp: number }>;
    monthly: Array<{ month: string; electricity_costs_gbp: number }>;
  };
  mileage?: {
    daily: Array<{ date: string; total_miles: number }>;
    weekly: Array<{ week_start: string; total_miles: number }>;
    monthly: Array<{ month: string; total_miles: number }>;
  };
  scope1_mileage?: {
    daily: Array<{ date: string; total_miles: number }>;
    weekly: Array<{ week_start: string; total_miles: number }>;
    monthly: Array<{ month: string; total_miles: number }>;
  };
  scope2_mileage?: {
    daily: Array<{ date: string; total_miles: number }>;
    weekly: Array<{ week_start: string; total_miles: number }>;
    monthly: Array<{ month: string; total_miles: number }>;
  };
}

interface Scope3EmissionsData {
  summary: {
    total_scope3_emissions_kg: number;
    total_orders: number;
    avg_scope3_per_order_kg: number;
    total_distance_m?: number;
    total_distance_miles?: number;
    avg_distance_m?: number;
    avg_distance_miles?: number;
  };
  by_transport_service: Array<{
    transport_service: string;
    total_scope3_emissions_kg: number;
    order_count: number;
    avg_scope3_per_order_kg: number;
    total_distance_m?: number;
    total_distance_miles?: number;
    avg_distance_m?: number;
    avg_distance_miles?: number;
    co2e_per_mile?: number;
  }>;
  by_origin_destination_pair: Array<{
    origin_destination_pair: string;
    total_scope3_emissions_kg: number;
    order_count: number;
    avg_scope3_per_order_kg: number;
    origin_city: string;
    destination_city: string;
    total_distance_m?: number;
    total_distance_miles?: number;
    avg_distance_m?: number;
    avg_distance_miles?: number;
    co2e_per_mile?: number;
  }>;
  metadata: {
    orders_csv_path: string;
    cancelled_orders_filtered: number;
    valid_orders_processed: number;
    has_distance_data?: boolean;
    generated_at: string;
  };
}

export default function CarbonReporting() {
  const { selectedPeriod } = usePeriod();
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  // ✅ Convert useCase to API serviceType: 'emergency' -> 'emergency_services', 'logistics' -> 'logistics'
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  
  // Subtabs state
  const [activeSection, setActiveSection] = useState<CarbonReportingSection>('fleet');
  const [vehicleTypeLevelCSV, setVehicleTypeLevelCSV] = useState<CSVRow[]>([]);
  const [vehicleLevelCSV, setVehicleLevelCSV] = useState<CSVRow[]>([]);
  const [stationLevelCSV, setStationLevelCSV] = useState<CSVRow[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string | null>(null); // null means "All"
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [scope3Data, setScope3Data] = useState<Scope3EmissionsData | null>(null);
  const [scope3Loading, setScope3Loading] = useState(false);
  const [selectedTimeInterval, _setSelectedTimeInterval] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [showPrimaryDataTooltip, setShowPrimaryDataTooltip] = useState(false);
  const [primaryDataTooltipPosition, setPrimaryDataTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const primaryDataRef = useRef<HTMLSpanElement>(null);
  const [showEstimatedTooltip, setShowEstimatedTooltip] = useState(false);
  const [estimatedTooltipPosition, setEstimatedTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const estimatedRef = useRef<HTMLSpanElement>(null);
  // Tooltip states for vehicle icon tooltips
  const [showVehiclePrimaryTooltip, setShowVehiclePrimaryTooltip] = useState<{ vehicleId: string; position: { x: number; y: number } } | null>(null);
  const [showVehicleEstimatedTooltip, setShowVehicleEstimatedTooltip] = useState<{ vehicleId: string; position: { x: number; y: number } } | null>(null);
  // State for methodology documentation modal
  const [showMethodologyModal, setShowMethodologyModal] = useState(false);
  // State for benchmark data
  const [benchmarkConfig, setBenchmarkConfig] = useState<any>(null);
  
  // Manual fuel/energy entry form state
  const [showManualCarbonForm, setShowManualCarbonForm] = useState(false);
  const [manualCarbonForm, setManualCarbonForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    vehicle_id: '',
    vehicle_type: '',
    fuel_type: 'Diesel',
    quantity: '',
    cost: '',
    odometer: '',
    notes: ''
  });
  const [submittingCarbon, setSubmittingCarbon] = useState(false);
  const [carbonSubmitSuccess, setCarbonSubmitSuccess] = useState(false);
  const [carbonSubmitError, setCarbonSubmitError] = useState<string | null>(null);
  const [manualCarbonEntries, setManualCarbonEntries] = useState<any[]>([]);
  const [selectedCarbonEntries, setSelectedCarbonEntries] = useState<Set<number>>(new Set());
  const [deleteCarbonConfirm, setDeleteCarbonConfirm] = useState<number[] | null>(null);
  const [newVehicleIdCarbon, setNewVehicleIdCarbon] = useState('');
  const [newVehicleTypeCarbon, setNewVehicleTypeCarbon] = useState('');
  const [showNewVehicleTypeInputCarbon, setShowNewVehicleTypeInputCarbon] = useState(false);

  const parseCSV = (text: string): CSVRow[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row: CSVRow = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }

    return rows;
  };

  // Helper function to identify electric vehicles
  const isElectricVehicle = (vehicleType: string): boolean => {
    if (!vehicleType) return false;
    const lowerType = vehicleType.toLowerCase();
    return lowerType.includes('bev') || 
           lowerType.includes('ze') || 
           lowerType.includes('electric') ||
           lowerType.includes('ev');
  };

  // Helper function to calculate total emissions (active + idle) from a CSV row
  // Must be defined before getFleetBenchmarkComparison which uses it
  const getTotalEmissionsFromRow = (csvRow: CSVRow): number => {
    const active = parseFloat(csvRow.active_emission_kg || '0');
    const idle = parseFloat(csvRow.idle_emission_kg || '0');
    return active + idle;
  };

  // Helper function to check if a CSV row has primary data
  const isPrimaryData = (row: CSVRow): boolean => {
    // Check if we have telematics data (primary) vs fallback calculations
    const hasTelematics = Boolean(
      (row.total_miles && parseFloat(row.total_miles) > 0) ||
      (row.Total_Miles && parseFloat(row.Total_Miles) > 0)
    );
    
    // Check if we have correction factors (indicates primary data processing)
    // For Logistics: speed, gradient, or laden corrections
    // For ES: speed corrections (gradient and laden not applicable)
    const hasSpeedCorrection = Boolean(
      row.avg_speed_correction && parseFloat(row.avg_speed_correction) !== 1.0
    );
    const hasGradientCorrection = Boolean(
      row.avg_gradient_correction && parseFloat(row.avg_gradient_correction) !== 1.0
    );
    const hasLadenPercentage = Boolean(
      row.avg_laden_percentage && 
      typeof row.avg_laden_percentage === 'string' && 
      row.avg_laden_percentage.toLowerCase() !== 'unavailable' &&
      !isNaN(parseFloat(row.avg_laden_percentage)) &&
      parseFloat(row.avg_laden_percentage) > 0
    );
    
    // Primary data if we have telematics AND at least one correction factor
    const hasCorrections = hasSpeedCorrection || hasGradientCorrection || hasLadenPercentage;

    return hasTelematics && hasCorrections;
  };

  // Get minimal default benchmarks if config file is not available
  // Note: These are baseline estimates from UK Government GHG Conversion Factors, not actual industry survey data
  // The backend config file should be used for accurate benchmarks
  const getDefaultBenchmarks = (serviceType: string) => {
    // Return minimal defaults only if backend config is unavailable
    // These values are baseline estimates, not actual industry averages
    if (serviceType === 'logistics') {
      return {
        co2e_per_mile: {
          industry_average: 1.2, // Baseline estimate from UK Government GHG Conversion Factors 2025
          percentiles: {
            p25: 1.0,
            p50: 1.2,
            p75: 1.4,
            p90: 1.6
          },
          source: 'UK Government GHG Conversion Factors 2025 (baseline estimate - not actual industry data)',
          last_updated: '2025',
          is_estimate: true
        }
      };
    } else {
      // Emergency Services
      return {
        co2e_per_mile: {
          industry_average: 0.8, // Baseline estimate from UK Government GHG Conversion Factors 2025
          percentiles: {
            p25: 0.7,
            p50: 0.8,
            p75: 0.9,
            p90: 1.0
          },
          source: 'UK Government GHG Conversion Factors 2025 (baseline estimate - not actual industry data)',
          last_updated: '2025',
          is_estimate: true
        }
      };
    }
  };

  // Calculate fleet performance vs benchmarks
  const getFleetBenchmarkComparison = () => {
    try {
      if (!benchmarkConfig || !vehicleTypeLevelCSV || vehicleTypeLevelCSV.length === 0) return null;

    // Calculate fleet average CO2e per mile
      const totalEmissions = vehicleTypeLevelCSV.reduce((sum, row) => {
        try {
          return sum + getTotalEmissionsFromRow(row);
        } catch (err) {
          logger.debug('Error calculating emissions from row:', err);
          return sum;
        }
      }, 0);
    const totalMiles = vehicleTypeLevelCSV.reduce((sum, row) => {
        try {
      const miles = parseFloat(row.total_miles || row.Total_Miles || row.miles || row.Miles || '0');
          return sum + (isNaN(miles) ? 0 : miles);
        } catch (err) {
          logger.debug('Error parsing miles from row:', err);
          return sum;
        }
    }, 0);
    
    const fleetCo2ePerMile = totalMiles > 0 ? totalEmissions / totalMiles : 0;
    const industryAverage = benchmarkConfig.co2e_per_mile?.industry_average || 0;
    
    // Calculate percentile
    const percentiles = benchmarkConfig.co2e_per_mile?.percentiles || {};
    let percentile = 50; // Default to median
    if (fleetCo2ePerMile <= percentiles.p25) percentile = 25;
    else if (fleetCo2ePerMile <= percentiles.p50) percentile = 50;
    else if (fleetCo2ePerMile <= percentiles.p75) percentile = 75;
    else if (fleetCo2ePerMile <= percentiles.p90) percentile = 90;
    else percentile = 95;

    // Calculate percentage difference from industry average
    // Flipped sign: positive = better (lower emissions), negative = worse (higher emissions)
    const percentDifference = industryAverage > 0 
      ? ((industryAverage - fleetCo2ePerMile) / industryAverage) * 100 
      : 0;

    // Determine performance status
    let performanceStatus: 'excellent' | 'good' | 'average' | 'below_average' = 'average';
    if (percentDifference >= 20) performanceStatus = 'excellent';
    else if (percentDifference >= 10) performanceStatus = 'good';
    else if (percentDifference >= -10) performanceStatus = 'average';
    else performanceStatus = 'below_average';

    return {
      fleetCo2ePerMile: fleetCo2ePerMile,
      industryAverage: industryAverage,
      percentDifference: percentDifference,
      percentile: percentile,
      performanceStatus: performanceStatus,
      source: benchmarkConfig.co2e_per_mile?.source || 'Industry estimates',
      lastUpdated: benchmarkConfig.co2e_per_mile?.last_updated || '2025'
    };
    } catch (error) {
      logger.error('Error in getFleetBenchmarkComparison:', error);
      return null;
    }
  };

  // Calculate data quality metrics
  const getDataQualityMetrics = () => {
    try {
      if (!vehicleLevelCSV || vehicleLevelCSV.length === 0) {
      return { primaryData: 0, estimatedData: 0, coverage: 0 };
    }

    let primaryCount = 0;
    let totalCount = vehicleLevelCSV.length;

    vehicleLevelCSV.forEach(row => {
        try {
      if (isPrimaryData(row)) {
        primaryCount++;
          }
        } catch (err) {
          logger.debug('Error checking if row is primary data:', err);
      }
    });

    const estimatedCount = totalCount - primaryCount;
    const coverage = totalCount > 0 ? (primaryCount / totalCount) * 100 : 0;

    return {
      primaryData: primaryCount,
      estimatedData: estimatedCount,
      coverage: Math.round(coverage)
    };
    } catch (error) {
      logger.error('Error in getDataQualityMetrics:', error);
      return { primaryData: 0, estimatedData: 0, coverage: 0 };
    }
  };

  useEffect(() => {
    // Reset all state when switching modes or periods
    setVehicleTypeLevelCSV([]);
    setVehicleLevelCSV([]);
    setStationLevelCSV([]);
    setSelectedVehicleType(null);
    setTimeSeriesData(null);
      setLoading(true);
    setWeekOffset(0);

    if (!selectedPeriod) {
      setLoading(false);
      return;
    }

    const period = selectedPeriod as string;
    let isCancelled = false; // Flag to prevent state updates if component unmounts or mode changes

      // ✅ Load data with serviceType for data isolation
      authenticatedFetch(API_ENDPOINTS.dashboardTimeSeries(period, serviceType), {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
        .then(res => {
          if (isCancelled) return null;
          if (!res.ok) {
            // If 404, that's okay - data might not exist for this period
            if (res.status === 404) {
              logger.debug(`Time-series data not available for period ${period} in ${serviceType} mode`);
              return null;
            }
            throw new Error(`Failed to load time-series data: ${res.status} ${res.statusText}`);
          }
          return res.json();
        })
        .then((data: TimeSeriesData | null) => {
          if (isCancelled) return;
          if (data) {
          logger.debug(`Loaded carbon time-series data for ${period}:`, data);
          // Debug: Check if emissions and fuel_costs are empty
          if (data.emissions && data.emissions.daily && data.emissions.daily.length === 0) {
            logger.warn(`[CarbonReporting] Empty emissions data for period ${period} in ${serviceType} mode`);
          }
          if (data.fuel_costs && data.fuel_costs.daily && data.fuel_costs.daily.length === 0) {
            logger.warn(`[CarbonReporting] Empty fuel_costs data for period ${period} in ${serviceType} mode`);
          }
          setTimeSeriesData(data);
          } else {
            // Data not available - set to null and continue
            setTimeSeriesData(null);
          }
          setLoading(false);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading time-series data:', err);
          // Set to null instead of leaving undefined
          setTimeSeriesData(null);
          setLoading(false);
        });

      authenticatedFetch(API_ENDPOINTS.vehicleTypeLevelCSV(period, serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          return res.text();
        })
        .then((text: string | undefined) => {
          if (isCancelled || !text) return;
          setVehicleTypeLevelCSV(parseCSV(text));
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading vehicle type level CSV:', err);
          setVehicleTypeLevelCSV([]);
        });

      // Load vehicle level CSV for Utilisation Metrics section
      authenticatedFetch(API_ENDPOINTS.vehicleLevelCSV(period, serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          return res.text();
        })
        .then((text: string | undefined) => {
          if (isCancelled || !text) return;
          setVehicleLevelCSV(parseCSV(text));
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading vehicle level CSV:', err);
          setVehicleLevelCSV([]);
        });

      // Load station level CSV (Emergency Services only)
      if (serviceType === 'emergency_services') {
        authenticatedFetch(API_ENDPOINTS.stationLevelCSV(period, serviceType))
          .then((res: Response) => {
            if (isCancelled) return;
            if (!res.ok) {
              // Station level CSV might not exist, that's okay
              return null;
            }
            return res.text();
          })
          .then((text: string | undefined | null) => {
            if (isCancelled) return;
            if (text) {
              setStationLevelCSV(parseCSV(text));
            } else {
              setStationLevelCSV([]);
            }
          })
          .catch(err => {
            if (isCancelled) return;
            // Station level CSV might not exist, that's okay - just log and continue
            logger.debug('Station level CSV not available:', err.message);
            setStationLevelCSV([]);
          });
      }

      // Load Scope 3 emissions data (Logistics only - Scope 3 is from orders/transport services)
      if (serviceType === 'logistics') {
        setScope3Loading(true);
        authenticatedFetch(API_ENDPOINTS.scope3Emissions(period, serviceType), {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        })
          .then((res: Response) => {
            if (isCancelled) return;
            if (!res.ok) {
              // Scope 3 data might not exist, that's okay
              if (res.status === 404) {
                logger.debug('Scope 3 emissions data not available for this period');
                return null;
              }
              throw new Error(`Failed to load Scope 3 emissions: ${res.status}`);
            }
            return res.json();
          })
          .then((data: Scope3EmissionsData | null) => {
            if (isCancelled) return;
            if (data) {
              logger.debug(`Loaded Scope 3 emissions data for ${period}:`, data);
              setScope3Data(data);
            } else {
              setScope3Data(null);
            }
            setScope3Loading(false);
          })
          .catch(err => {
            if (isCancelled) return;
            // Scope 3 data might not exist, that's okay - just log and continue
            logger.debug('Scope 3 emissions data not available:', err.message);
            setScope3Data(null);
            setScope3Loading(false);
          });
      } else {
        // ES mode doesn't have Scope 3 data
        setScope3Data(null);
        setScope3Loading(false);
      }

      // Load benchmark configuration
      authenticatedFetch(API_ENDPOINTS.benchmarkConfig(serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          if (!res.ok) {
            // Benchmark config might not exist, that's okay - use defaults
            if (res.status === 404) {
              logger.debug('Benchmark config not available, using defaults');
              return null;
            }
            throw new Error(`Failed to load benchmark config: ${res.status}`);
          }
          return res.json();
        })
        .then((data: any | null) => {
          if (isCancelled) return;
          if (data) {
            logger.debug(`Loaded benchmark config for ${serviceType}:`, data);
            setBenchmarkConfig(data);
          } else {
            // Use default benchmarks if config not available
            setBenchmarkConfig(getDefaultBenchmarks(serviceType));
          }
        })
        .catch(err => {
          if (isCancelled) return;
          logger.debug('Benchmark config not available, using defaults:', err.message);
          setBenchmarkConfig(getDefaultBenchmarks(serviceType));
    });
    
    // Cleanup function to cancel in-flight requests when mode/period changes
    return () => {
      isCancelled = true;
    };
  }, [selectedPeriod, serviceType]); // ✅ Include serviceType in dependencies

  // Helper function to get common date range from time series data
  // Defined before useEffect to avoid ReferenceError
  const getCommonDateRange = (): string[] => {
    if (!timeSeriesData) return [];
    if (!timeSeriesData.emissions || !timeSeriesData.emissions.daily) return [];
    if (!timeSeriesData.fuel_costs || !timeSeriesData.fuel_costs.daily) return [];
    
    const normalizeDate = (dateStr: string) => {
      try {
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    try {
      const emissionsDates = (timeSeriesData.emissions?.daily || []).map(item => normalizeDate(item?.date || '')).filter(Boolean);
      const fuelCostsDates = (timeSeriesData.fuel_costs?.daily || []).map(item => normalizeDate(item?.date || '')).filter(Boolean);
      const dieselCostsDates = timeSeriesData.diesel_fuel_costs?.daily?.map(item => normalizeDate(item.date)).filter(Boolean) || [];
      const electricityCostsDates = timeSeriesData.electricity_costs?.daily?.map(item => normalizeDate(item.date)).filter(Boolean) || [];
      const mileageDates = timeSeriesData.mileage?.daily?.map(item => normalizeDate(item.date)).filter(Boolean) || [];

      const allDateSets = [emissionsDates, fuelCostsDates, dieselCostsDates, electricityCostsDates, mileageDates].filter(set => set.length > 0);
      if (allDateSets.length === 0) return [];
      
      let commonDates = allDateSets[0] || [];
      for (const set of allDateSets) {
        commonDates = commonDates.filter(date => set.includes(date));
      }
      return commonDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    } catch (error) {
      logger.error('Error in getCommonDateRange:', error);
      return [];
    }
  };

  useEffect(() => {
    // Reset week offset when switching modes, periods, or time intervals
    if (selectedTimeInterval !== 'daily') {
      setWeekOffset(0);
    } else if (timeSeriesData) {
      // When switching to daily view or period changes, show earliest week first
      try {
      const commonDates = getCommonDateRange();
      if (commonDates.length > 7) {
        const totalWeeks = Math.ceil(commonDates.length / 7);
        setWeekOffset(-(totalWeeks - 1)); // Set to earliest week (index 0)
      } else {
          setWeekOffset(0);
        }
      } catch (error) {
        logger.error('Error calculating common date range:', error);
      setWeekOffset(0);
    }
    } else {
      setWeekOffset(0);
    }
  }, [selectedTimeInterval, selectedPeriod, serviceType, timeSeriesData]);

  const _getTotalEmissions = () => {
    const total = vehicleTypeLevelCSV.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
    return total >= 1000 ? `${(total / 1000).toFixed(1)}t` : `${total.toFixed(1)}kg`;
  };

  const getTotalFuelCost = () => {
    const total = vehicleTypeLevelCSV.reduce((sum, row) => {
      const fuelCost = parseFloat(row.active_fuel_cost_GBP || '0');
      const electricityCost = parseFloat(row.electricity_cost_GBP || '0');
      return sum + fuelCost + electricityCost;
    }, 0);
    return total >= 1000 ? `£${(total / 1000).toFixed(1)}K` : `£${total.toFixed(2)}`;
  };

  const getTotalFuelUsage = () => {
    const total = vehicleTypeLevelCSV.reduce((sum, row) => {
      const fuelUsage = parseFloat(row.active_fuel_litre || row.active_fuel_liters || '0');
      return sum + fuelUsage;
    }, 0);
    return `${total.toFixed(1)} L`;
  };

  const getTotalEnergyConsumption = () => {
    const total = vehicleTypeLevelCSV.reduce((sum, row) => {
      const energy = parseFloat(row.energy_consumption_kWh || row.energy_consumption_kwh || '0');
      return sum + energy;
    }, 0);
    return `${total.toFixed(1)} kWh`;
  };

  // Generate comprehensive PDF report (reserved for future export)
  const _generatePDFReport = () => {
    const qualityMetrics = getDataQualityMetrics();
    const scope1Data = vehicleTypeLevelCSV.filter(row => !isElectricVehicle(row.Vehicle_Type || ''));
    const scope2Data = vehicleTypeLevelCSV.filter(row => isElectricVehicle(row.Vehicle_Type || ''));
    const scope1Emissions = scope1Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
    const scope2Emissions = scope2Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
    const totalEmissions = scope1Emissions + scope2Emissions;
    const scope3Emissions = scope3Data?.summary?.total_scope3_emissions_kg || 0;
    const benchmarkComparison = getFleetBenchmarkComparison();

    const doc = new jsPDF();
    let yPos = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const footerHeight = 15; // Space reserved for footer

    // Helper function to add a new page if needed (accounts for footer)
    const checkPageBreak = (requiredSpace: number) => {
      if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - margin - footerHeight) {
        doc.addPage();
        yPos = margin;
      }
    };

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Carbon Emissions Report', margin, yPos);
    yPos += 10;

    // Period and Service Type
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${selectedPeriod || 'All'}`, margin, yPos);
    yPos += 6;
    doc.text(`Service Type: ${serviceType === 'logistics' ? 'Logistics' : 'Emergency Services'}`, margin, yPos);
    yPos += 6;
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, yPos);
    yPos += 15;

    // Data Quality & Verification
    checkPageBreak(30);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Data Quality & Verification', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Primary Data: ', margin, yPos);
    const primaryLabelWidth = doc.getTextWidth('Primary Data: ');
    doc.setFont('helvetica', 'bold');
    doc.text(`${qualityMetrics.primaryData} vehicles`, margin + primaryLabelWidth, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    doc.text('Estimated Data: ', margin, yPos);
    const estimatedLabelWidth = doc.getTextWidth('Estimated Data: ');
    doc.setFont('helvetica', 'bold');
    doc.text(`${qualityMetrics.estimatedData} vehicles`, margin + estimatedLabelWidth, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    doc.text('Coverage: ', margin, yPos);
    const coverageLabelWidth = doc.getTextWidth('Coverage: ');
    doc.setFont('helvetica', 'bold');
    doc.text(`${qualityMetrics.coverage}% primary`, margin + coverageLabelWidth, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    if (qualityMetrics.coverage >= 80) {
      doc.setTextColor(0, 150, 0);
      doc.text('Audit-Ready | CSRD Compliant', margin, yPos);
      doc.setTextColor(0, 0, 0);
    }
    yPos += 8;
    doc.text('Methodology: GLEC Framework aligned, ISO 14083 compliant', margin, yPos);
    yPos += 6;
    doc.text('Emission Factors: UK Government GHG Conversion Factors 2025', margin, yPos);
    yPos += 15;

    // Summary KPIs
    checkPageBreak(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Scope 1 (Direct): ', margin, yPos);
    const scope1LabelWidth = doc.getTextWidth('Scope 1 (Direct): ');
    doc.setFont('helvetica', 'bold');
    doc.text(`${scope1Emissions >= 1000 ? `${(scope1Emissions / 1000).toFixed(3)} tonnes` : `${scope1Emissions.toFixed(3)} kg`}`, margin + scope1LabelWidth, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    if (serviceType === 'logistics' && scope2Emissions > 0) {
      doc.text('Scope 2 (Indirect): ', margin, yPos);
      const scope2LabelWidth = doc.getTextWidth('Scope 2 (Indirect): ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${scope2Emissions >= 1000 ? `${(scope2Emissions / 1000).toFixed(3)} tonnes` : `${scope2Emissions.toFixed(3)} kg`}`, margin + scope2LabelWidth, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
    }
    if (scope3Emissions > 0) {
      doc.text('Scope 3 (Well-to-Tank, Partial): ', margin, yPos);
      const scope3LabelWidth = doc.getTextWidth('Scope 3 (Upstream): ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${scope3Emissions >= 1000 ? `${(scope3Emissions / 1000).toFixed(3)} tonnes` : `${scope3Emissions.toFixed(3)} kg`}`, margin + scope3LabelWidth, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
    }
    doc.text('Total CO2e Emissions: ', margin, yPos);
    const totalEmissionsLabelWidth = doc.getTextWidth('Total CO2e Emissions: ');
    doc.setFont('helvetica', 'bold');
    doc.text(`${totalEmissions >= 1000 ? `${(totalEmissions / 1000).toFixed(3)} tonnes` : `${totalEmissions.toFixed(3)} kg`}`, margin + totalEmissionsLabelWidth, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    doc.text(`Total Fuel Cost: ${getTotalFuelCost()}`, margin, yPos);
    yPos += 6;
    doc.text(`Total Vehicles: ${vehicleLevelCSV.length}`, margin, yPos);
    yPos += 15;

    // Benchmark Comparison
    if (benchmarkComparison) {
      checkPageBreak(40);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance vs Industry Benchmarks', margin, yPos);
      yPos += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Fleet CO2e/mile: ', margin, yPos);
      const fleetLabelWidth = doc.getTextWidth('Fleet CO2e/mile: ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${benchmarkComparison.fleetCo2ePerMile.toFixed(4)} kg CO2e/mile`, margin + fleetLabelWidth, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
      doc.text('Industry Average: ', margin, yPos);
      const industryLabelWidth = doc.getTextWidth('Industry Average: ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${benchmarkComparison.industryAverage.toFixed(4)} kg CO2e/mile`, margin + industryLabelWidth, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
      const diffText = benchmarkComparison.percentDifference > 0 ? '+' : '';
      const isBetter = benchmarkComparison.percentDifference > 0;
      const isWorse = benchmarkComparison.percentDifference < 0;
      // Format: "Performance: +76.74% (BETTER: excellent)" with BETTER in green if better
      doc.text('Performance: ', margin, yPos);
      const performanceLabelWidth = doc.getTextWidth('Performance: ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${diffText}${benchmarkComparison.percentDifference.toFixed(2)}% (`, margin + performanceLabelWidth, yPos);
      const performanceValueWidth = doc.getTextWidth(`${diffText}${benchmarkComparison.percentDifference.toFixed(2)}% (`);
      if (isBetter) {
        doc.setTextColor(0, 150, 0); // Green
        doc.text('BETTER', margin + performanceLabelWidth + performanceValueWidth, yPos);
        const betterWidth = doc.getTextWidth('BETTER');
        doc.setTextColor(0, 0, 0); // Reset to black
        doc.text(`: ${benchmarkComparison.performanceStatus})`, margin + performanceLabelWidth + performanceValueWidth + betterWidth, yPos);
      } else if (isWorse) {
        doc.text('WORSE', margin + performanceLabelWidth + performanceValueWidth, yPos);
        const worseWidth = doc.getTextWidth('WORSE');
        doc.text(`: ${benchmarkComparison.performanceStatus})`, margin + performanceLabelWidth + performanceValueWidth + worseWidth, yPos);
      } else {
        doc.text('EQUAL', margin + performanceLabelWidth + performanceValueWidth, yPos);
        const equalWidth = doc.getTextWidth('EQUAL');
        doc.text(`: ${benchmarkComparison.performanceStatus})`, margin + performanceLabelWidth + performanceValueWidth + equalWidth, yPos);
      }
      doc.setFont('helvetica', 'normal');
      yPos += 6;
      doc.text('Percentile Ranking: ', margin, yPos);
      const percentileLabelWidth = doc.getTextWidth('Percentile Ranking: ');
      doc.setFont('helvetica', 'bold');
      doc.text(`${benchmarkComparison.percentile}th percentile`, margin + percentileLabelWidth, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
      doc.text(`Source: ${benchmarkComparison.source}`, margin, yPos);
      yPos += 15;
    }

    // Scope Breakdown - Display in 3 columns side by side
    checkPageBreak(50);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Scope Breakdown', margin, yPos);
    yPos += 10;
    
    // Calculate column positions
    const columnWidth = (pageWidth - (margin * 2)) / 3;
    const col1X = margin;
    const col2X = margin + columnWidth;
    const col3X = margin + (columnWidth * 2);
    const lineHeight = 6;
    let maxLines = 0;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Scope 1 Summary - count actual vehicles, not vehicle types
    const scope1Vehicles = vehicleLevelCSV.filter(row => !isElectricVehicle(row.Vehicle_Type || ''));
    let scope1Lines: string[] = [];
    if (scope1Vehicles.length > 0) {
      const scope1TotalMiles = scope1Vehicles.reduce((sum, row) => sum + parseFloat(row.total_miles || row.Total_Miles || '0'), 0);
      const scope1Co2ePerMile = scope1TotalMiles > 0 ? scope1Emissions / scope1TotalMiles : 0;
      // Header: "Scope" in red, "1 (Diesel)" in red and bold
      doc.setTextColor(255, 0, 0); // Red
      doc.setFont('helvetica', 'normal');
      doc.text('Scope ', col1X, yPos);
      const scope1HeaderWidth = doc.getTextWidth('Scope ');
      doc.setFont('helvetica', 'bold');
      doc.text('1 (Diesel)', col1X + scope1HeaderWidth, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0); // Reset to black for content
      scope1Lines = [
        `Total CO2e: ${scope1Emissions >= 1000 ? `${(scope1Emissions / 1000).toFixed(3)}t` : `${scope1Emissions.toFixed(3)} kg`}`,
        `Vehicles: ${scope1Vehicles.length}`,
        `Miles: ${scope1TotalMiles.toFixed(1)}`,
        `CO2e/mile: ${scope1Co2ePerMile.toFixed(4)} kg`
      ];
      maxLines = Math.max(maxLines, scope1Lines.length);
    }

    // Scope 2 Summary - count actual vehicles, not vehicle types
    const scope2Vehicles = vehicleLevelCSV.filter(row => isElectricVehicle(row.Vehicle_Type || ''));
    let scope2Lines: string[] = [];
    if (serviceType === 'logistics' && scope2Vehicles.length > 0) {
      const scope2TotalMiles = scope2Vehicles.reduce((sum, row) => sum + parseFloat(row.total_miles || row.Total_Miles || '0'), 0);
      const scope2Co2ePerMile = scope2TotalMiles > 0 ? scope2Emissions / scope2TotalMiles : 0;
      // Header: "Scope" in red, "2 (Electric)" in red and bold
      doc.setTextColor(255, 0, 0); // Red
      doc.setFont('helvetica', 'normal');
      doc.text('Scope ', col2X, yPos);
      const scope2HeaderWidth = doc.getTextWidth('Scope ');
      doc.setFont('helvetica', 'bold');
      doc.text('2 (Electric)', col2X + scope2HeaderWidth, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0); // Reset to black for content
      scope2Lines = [
        `Total CO2e: ${scope2Emissions >= 1000 ? `${(scope2Emissions / 1000).toFixed(3)}t` : `${scope2Emissions.toFixed(3)} kg`}`,
        `Vehicles: ${scope2Vehicles.length}`,
        `Miles: ${scope2TotalMiles.toFixed(1)}`,
        `CO2e/mile: ${scope2Co2ePerMile.toFixed(4)} kg`
      ];
      maxLines = Math.max(maxLines, scope2Lines.length);
    }

    // Scope 3 Summary
    let scope3Lines: string[] = [];
    if (scope3Emissions > 0 && scope3Data?.summary) {
      const totalDistance = scope3Data.summary.total_distance_miles || scope3Data.summary.total_distance_m || 0;
      const co2ePerMile = totalDistance > 0 ? scope3Emissions / totalDistance : 0;
      // Header: "Scope" in red, "3 (Upstream)" in red and bold
      doc.setTextColor(255, 0, 0); // Red
      doc.setFont('helvetica', 'normal');
      doc.text('Scope ', col3X, yPos);
      const scope3HeaderWidth = doc.getTextWidth('Scope ');
      doc.setFont('helvetica', 'bold');
      doc.text('3 (Well-to-Tank, Partial)', col3X + scope3HeaderWidth, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0); // Reset to black for content
      scope3Lines = [
        `Total CO2e: ${scope3Emissions >= 1000 ? `${(scope3Emissions / 1000).toFixed(3)}t` : `${scope3Emissions.toFixed(3)} kg`}`,
        `Orders: ${scope3Data.summary.total_orders}`,
        `Avg CO2e/Order: ${scope3Data.summary.avg_scope3_per_order_kg.toFixed(4)} kg`,
      ];
      if (totalDistance > 0) {
        scope3Lines.push(`Miles: ${totalDistance.toFixed(1)}`);
        scope3Lines.push(`CO2e/mile: ${co2ePerMile.toFixed(4)} kg`);
      }
      maxLines = Math.max(maxLines, scope3Lines.length);
    }
    
    // Draw the data lines
    yPos += lineHeight;
    for (let i = 0; i < maxLines; i++) {
      if (scope1Lines[i]) {
        doc.text(scope1Lines[i], col1X, yPos);
      }
      if (scope2Lines[i]) {
        doc.text(scope2Lines[i], col2X, yPos);
      }
      if (scope3Lines[i]) {
        doc.text(scope3Lines[i], col3X, yPos);
      }
      yPos += lineHeight;
    }
    yPos += 10;

    // Methodology
    checkPageBreak(50);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Methodology', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Framework: GLEC Framework aligned', margin, yPos);
    yPos += 6;
    doc.text('Standard: ISO 14083 compliant', margin, yPos);
    yPos += 6;
    doc.text('Emission Factors: UK Government GHG Conversion Factors 2025', margin, yPos);
    yPos += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('This report is generated using verified telematics data where available,', margin, yPos);
    yPos += 5;
    doc.text('with fallback to estimated calculations when primary data is unavailable.', margin, yPos);
    yPos += 5;
    doc.text('All calculations follow GLEC Framework and ISO 14083 standards.', margin, yPos);

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, doc.internal.pageSize.getHeight() - 10);
      doc.text('ZEEFLEET Carbon Reporting', margin, doc.internal.pageSize.getHeight() - 10);
    }

    // Save PDF
    const filename = `carbon_report_${selectedPeriod || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  const filterDailyDataByWeek = <T extends { date?: string }>(data: T[], commonDates: string[]): T[] => {
    if (data.length === 0 || commonDates.length === 0) return [];
    const normalizeDate = (dateStr: string) => {
      const date = new Date(dateStr || '');
      return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    const filtered = data.filter(item => item.date && commonDates.includes(normalizeDate(item.date)));
    const sorted = [...filtered].sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());

    const totalWeeks = Math.ceil(sorted.length / 7);
    const weekIndex = totalWeeks - 1 + weekOffset;
    if (weekIndex < 0 || weekIndex >= totalWeeks) return [];

    const startIndex = weekIndex * 7;
    return sorted.slice(startIndex, Math.min(startIndex + 7, sorted.length));
  };

  const _hasPreviousWeek = () => {
    if (selectedTimeInterval !== 'daily' || !timeSeriesData) return false;
    const commonDates = getCommonDateRange();
    const totalWeeks = Math.ceil(commonDates.length / 7);
    const weekIndex = totalWeeks - 1 + weekOffset;
    return weekIndex > 0;
  };

  const _hasNextWeek = () => {
    if (selectedTimeInterval !== 'daily' || !timeSeriesData) return false;
    const commonDates = getCommonDateRange();
    const totalWeeks = Math.ceil(commonDates.length / 7);
    const weekIndex = totalWeeks - 1 + weekOffset;
    return weekIndex < totalWeeks - 1;
  };

  // Helper function to format date range as "(1-7 July 2024)"
  const formatWeekDateRange = (weekOffsetDelta: number): string => {
    if (selectedTimeInterval !== 'daily' || !timeSeriesData) return '';
    const commonDates = getCommonDateRange();
    if (commonDates.length === 0) return '';
    
    const totalWeeks = Math.ceil(commonDates.length / 7);
    const weekIndex = totalWeeks - 1 + weekOffset + weekOffsetDelta;
    
    if (weekIndex < 0 || weekIndex >= totalWeeks) return '';
    
    const startIndex = weekIndex * 7;
    const weekDates = commonDates.slice(startIndex, Math.min(startIndex + 7, commonDates.length));
    
    if (weekDates.length === 0) return '';
    
    const startDate = new Date(weekDates[0]);
    const endDate = new Date(weekDates[weekDates.length - 1]);
    
    // Format: "(1-7 July 2024)"
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startMonth = startDate.toLocaleDateString('en-GB', { month: 'short' });
    const startYear = startDate.getFullYear();
    const endMonth = endDate.toLocaleDateString('en-GB', { month: 'short' });
    const endYear = endDate.getFullYear();
    
    // If same month and year, format as "(1-7 July 2024)"
    if (startDate.getMonth() === endDate.getMonth() && startYear === endYear) {
      return `${startDay}-${endDay} ${startMonth} ${startYear}`;
    } else if (startYear === endYear) {
      // If same year but different months, format as "(1 July - 7 August 2024)"
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
    } else {
      // If different years, format as "(1 July 2024 - 7 August 2025)"
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }
  };

  const _getPreviousWeekLabel = (): string => {
    return formatWeekDateRange(-1);
  };

  const _getNextWeekLabel = (): string => {
    return formatWeekDateRange(1);
  };

  // Helper function to calculate scope-separated daily emissions
  // First tries to use timeSeriesData if available, otherwise calculates from vehicle-level CSV
  const calculateScopeSeparatedEmissions = () => {
    // First, try to use scope-separated data from timeSeriesData (if backend provides it)
    if (timeSeriesData?.scope1_emissions?.daily && timeSeriesData?.scope2_emissions?.daily) {
      return {
        scope1Daily: timeSeriesData.scope1_emissions.daily.map(d => ({
          date: d.date,
          total_emissions_kg_co2e: d.total_emissions_kg_co2e,
          total_miles: timeSeriesData.scope1_mileage?.daily?.find(m => m.date === d.date)?.total_miles || 0
        })),
        scope2Daily: timeSeriesData.scope2_emissions.daily.map(d => ({
          date: d.date,
          total_emissions_kg_co2e: d.total_emissions_kg_co2e,
          total_miles: timeSeriesData.scope2_mileage?.daily?.find(m => m.date === d.date)?.total_miles || 0
        }))
      };
    }

    // Fallback: Calculate from vehicle-level CSV if it has date information
    // Note: Vehicle-level CSV typically doesn't have per-day breakdowns, so this may not work
    // The backend script should be updated to generate scope-separated time series data
    if (vehicleLevelCSV.length === 0) return null;

    // Check if any row has date information
    const hasDateInfo = vehicleLevelCSV.some(row => 
      row.date || row.Date || row.trip_date || row.Trip_Date
    );

    if (!hasDateInfo) {
      // Vehicle-level CSV doesn't have date info - can't calculate daily breakdown
      // Return null to indicate we need backend to provide scope-separated data
      return null;
    }

    // Group by date and calculate scope 1 and scope 2 emissions
    const scope1ByDate = new Map<string, { emissions: number; miles: number }>();
    const scope2ByDate = new Map<string, { emissions: number; miles: number }>();

    vehicleLevelCSV.forEach(row => {
      const vehicleType = row.Vehicle_Type || '';
      const isEV = isElectricVehicle(vehicleType);
      
      // Try to get date from various possible column names
      const dateStr = row.date || row.Date || row.trip_date || row.Trip_Date || '';
      if (!dateStr) return;

      const emissions = getTotalEmissionsFromRow(row);
      const miles = parseFloat(row.total_miles || row.Total_Miles || row.miles || row.Miles || '0');

      if (isEV) {
        // Scope 2 (Electric)
        const existing = scope2ByDate.get(dateStr) || { emissions: 0, miles: 0 };
        scope2ByDate.set(dateStr, {
          emissions: existing.emissions + emissions,
          miles: existing.miles + miles
        });
      } else {
        // Scope 1 (Diesel)
        const existing = scope1ByDate.get(dateStr) || { emissions: 0, miles: 0 };
        scope1ByDate.set(dateStr, {
          emissions: existing.emissions + emissions,
          miles: existing.miles + miles
        });
      }
    });

    // Convert to arrays matching timeSeriesData format
    const scope1Daily = Array.from(scope1ByDate.entries())
      .map(([date, data]) => ({
        date,
        total_emissions_kg_co2e: data.emissions,
        total_miles: data.miles
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const scope2Daily = Array.from(scope2ByDate.entries())
      .map(([date, data]) => ({
        date,
        total_emissions_kg_co2e: data.emissions,
        total_miles: data.miles
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return { scope1Daily, scope2Daily };
  };

  // Get daily emissions data as multiple series (Scope 1 and Scope 2)
  const getDailyEmissionsSeries = () => {
    if (!timeSeriesData || !timeSeriesData.emissions || !timeSeriesData.emissions.daily) return [];

    // Calculate scope-separated data from vehicle-level CSV if not in timeSeriesData
    const scopeData = calculateScopeSeparatedEmissions();
    
    const series = [];

    // Scope 1 (Diesel) - Red
    if (scopeData && scopeData.scope1Daily.length > 0) {
      const scope1Points = scopeData.scope1Daily.map(item => {
        const dailyItem = item as { date: string; total_emissions_kg_co2e: number };
        let label = '';
        
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          // Format as day number only (e.g., "1", "15", "31")
          label = date.getDate().toString();
        }
        
        // Use total emissions (kg) instead of per-mile
        const totalEmissions = dailyItem.total_emissions_kg_co2e || 0;
        
        return {
          ...item,
          label,
          value: totalEmissions
        };
      });
      
      if (scope1Points.length > 0) {
        series.push({ 
          name: 'Scope 1 (Diesel)', 
          data: scope1Points, 
          color: CHART_PALETTE[3] // Red
        });
      }
    }

    // Scope 2 (Electric) - Blue
    if (scopeData && scopeData.scope2Daily.length > 0) {
      const scope2Points = scopeData.scope2Daily.map(item => {
        const dailyItem = item as { date: string; total_emissions_kg_co2e: number };
        let label = '';
        
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          // Format as day number only (e.g., "1", "15", "31")
          label = date.getDate().toString();
        }
        
        // Use total emissions (kg) instead of per-mile
        const totalEmissions = dailyItem.total_emissions_kg_co2e || 0;
        
        return {
          ...item,
          label,
          value: totalEmissions
        };
      });
      
      if (scope2Points.length > 0) {
        series.push({ 
          name: 'Scope 2 (Electric)', 
          data: scope2Points, 
          color: CHART_PALETTE[0] // Blue
        });
      }
    }

    // If no scope data available, fall back to total emissions
    if (series.length === 0 && timeSeriesData.emissions?.daily && timeSeriesData.emissions.daily.length > 0) {
      const totalPoints = timeSeriesData.emissions.daily.map(item => {
        const dailyItem = item as { date: string; total_emissions_kg_co2e: number };
        let label = '';
        
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          label = date.getDate().toString();
        }
        
        const totalEmissions = dailyItem.total_emissions_kg_co2e || 0;
        
        return {
          ...item,
          label,
          value: totalEmissions
        };
      });
      
      if (totalPoints.length > 0) {
        series.push({ 
          name: 'Total Emissions', 
          data: totalPoints, 
          color: CHART_PALETTE[3] // Red (default)
        });
      }
    }

    return series;
  };

  const _getEmissionsData = () => {
    if (!timeSeriesData) return [];
    const interval = selectedTimeInterval;
    let emissions = timeSeriesData.emissions?.[interval] || { daily: [], weekly: [], monthly: [] };
    if (!emissions) return [];

    if (interval === 'daily') {
      const commonDates = getCommonDateRange();
      emissions = filterDailyDataByWeek(emissions as Array<{ date: string }>, commonDates) as typeof emissions;
    }

    return emissions.map(item => {
      let label = '';
      let weekday = '';
      
      if (interval === 'daily' && (item as any).date) {
        const date = new Date((item as any).date);
        label = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
      } else if (interval === 'weekly' && (item as any).week_start) {
        label = new Date((item as any).week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      } else if (interval === 'monthly' && (item as any).month) {
        label = (item as any).month;
      }
      
      // Use total emissions (kg) instead of per-mile
      const totalEmissions = item.total_emissions_kg_co2e || 0;
      
      return {
        label,
        weekday,
        value: totalEmissions,
        ...item
      };
    });
  };

  const formatSeriesData = (data: Array<Record<string, any>>, valueKey: string) => {
    if (!data || data.length === 0) return [];
    let filteredData = data;
    if (selectedTimeInterval === 'daily' && data[0]?.date) {
      const commonDates = getCommonDateRange();
      filteredData = filterDailyDataByWeek(data as Array<{ date: string }>, commonDates);
    }

    return filteredData.map(item => {
      let label = '';
      let weekday = '';
      if (selectedTimeInterval === 'daily' && item.date) {
        const date = new Date(item.date);
        label = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
      } else if (selectedTimeInterval === 'weekly' && item.week_start) {
        label = new Date(item.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      } else if (selectedTimeInterval === 'monthly' && item.month) {
        label = item.month;
      }
      return {
        label,
        weekday,
        value: Number(item[valueKey]) || 0,
        ...item
      };
    });
  };

  // Get daily fuel costs series (all days, no week filtering)
  const getDailyFuelCostsSeries = () => {
    if (!timeSeriesData) return [];

    const series = [];

    if (timeSeriesData.diesel_fuel_costs?.daily) {
      const points = timeSeriesData.diesel_fuel_costs.daily.map(item => {
        const dailyItem = item as { date: string; diesel_fuel_costs_gbp: number };
        let label = '';
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          // Format as day number only (e.g., "1", "15", "31")
          label = date.getDate().toString();
        }
        // Use total cost (£) instead of per-mile
        return {
          label,
          value: dailyItem.diesel_fuel_costs_gbp || 0,
          ...item
        };
      });
      if (points.length > 0) {
        series.push({ name: 'Diesel Fuel Costs', data: points, color: CHART_PALETTE[1] });
      }
    }

    if (timeSeriesData.electricity_costs?.daily) {
      const points = timeSeriesData.electricity_costs.daily.map(item => {
        const dailyItem = item as { date: string; electricity_costs_gbp: number };
        let label = '';
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          // Format as day number only (e.g., "1", "15", "31")
          label = date.getDate().toString();
        }
        // Use total cost (£) instead of per-mile
        return {
          label,
          value: dailyItem.electricity_costs_gbp || 0,
          ...item
        };
      });
      if (points.length > 0) {
        series.push({ name: 'Electricity Costs', data: points, color: CHART_PALETTE[0] });
      }
    }

    if (series.length === 0 && timeSeriesData.fuel_costs?.daily) {
      const points = (timeSeriesData.fuel_costs?.daily || []).map(item => {
        const dailyItem = item as { date: string; total_fuel_costs_gbp: number };
        let label = '';
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          // Format as day number only (e.g., "1", "15", "31")
          label = date.getDate().toString();
        }
        // Use total cost (£) instead of per-mile
        return {
          label,
          value: dailyItem.total_fuel_costs_gbp || 0,
          ...item
        };
      });
      if (points.length > 0) {
        // Use different label based on mode: ES mode doesn't have electricity
        const label = serviceType === 'emergency_services' ? 'Total Fuel Costs' : 'Total Fuel & Electricity Costs';
        series.push({ name: label, data: points, color: CHART_PALETTE[1] });
      }
    }

    return series;
  };

  const _getFuelCostsSeries = () => {
    if (!timeSeriesData) return [];
    const interval = selectedTimeInterval;
    
    // Get mileage data for calculating cost per mile
    const mileage = timeSeriesData.mileage?.[interval] || [];
    
    // Create a map of mileage by date/week_start/month for quick lookup
    const mileageMap = new Map<string, number>();
    mileage.forEach(item => {
      let key = '';
      if (interval === 'daily' && (item as any).date) {
        key = (item as any).date;
      } else if (interval === 'weekly' && (item as any).week_start) {
        key = (item as any).week_start;
      } else if (interval === 'monthly' && (item as any).month) {
        key = (item as any).month;
      }
      if (key) {
        mileageMap.set(key, (item as any).total_miles || 0);
      }
    });

    const series = [];

    if (timeSeriesData.diesel_fuel_costs) {
      const points = formatSeriesData(timeSeriesData.diesel_fuel_costs[selectedTimeInterval] as Array<Record<string, any>>, 'diesel_fuel_costs_gbp');
      if (points.length > 0) {
        // Use total cost (£) instead of per-mile
        series.push({ name: 'Diesel Fuel Costs', data: points, color: CHART_PALETTE[1] });
      }
    }

    if (timeSeriesData.electricity_costs) {
      const points = formatSeriesData(timeSeriesData.electricity_costs[selectedTimeInterval] as Array<Record<string, any>>, 'electricity_costs_gbp');
      if (points.length > 0) {
        // Use total cost (£) instead of per-mile
        series.push({ name: 'Electricity Costs', data: points, color: CHART_PALETTE[0] });
      }
    }

    if (series.length === 0 && timeSeriesData.fuel_costs) {
      const points = formatSeriesData(timeSeriesData.fuel_costs[selectedTimeInterval] as Array<Record<string, any>>, 'total_fuel_costs_gbp');
      if (points.length > 0) {
        // Use total cost (£) instead of per-mile
        // Use different label based on mode: ES mode doesn't have electricity
        const label = serviceType === 'emergency_services' ? 'Total Fuel Costs' : 'Total Fuel & Electricity Costs';
        series.push({ name: label, data: points, color: CHART_PALETTE[1] });
      }
    }

    return series;
  };

  // Formatting functions for Utilisation Metrics
  const formatNumber = (value: string | number, decimals: number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const formatMiles = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num >= 100 ? formatNumber(value, 0) : formatNumber(value, 1);
  };
  const formatEmission = (value: string | number): string => formatNumber(value, 1);
  const formatFuel = (value: string | number): string => formatNumber(value, 1);
  const formatCurrency = (value: string | number): string => formatNumber(value, 1);
  const formatCount = (value: string | number): string => formatNumber(value, 0);

  // Calculate gradient color based on score (0-100)
  // For CO2e/mile: inverted scale - lower emissions = higher score = green, higher emissions = lower score = red
  const getScoreColor = (score: number) => {
    if (score <= 50) {
      // Red to Yellow gradient (0-50)
      const ratio = score / 50;
      const r = 255;
      const g = Math.round(ratio * 255);
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Yellow to Green gradient (50-100)
      const ratio = (score - 50) / 50;
      const r = Math.round(255 * (1 - ratio));
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Calculate CO2e/mile score (0-100) where lower emissions = higher score
  // Typical range: 0.1-0.5 kg CO2e/mile for diesel vehicles, 0.0-0.2 for electric
  const getCo2ePerMileScore = (co2ePerMile: number, allValues: number[]): number => {
    if (allValues.length === 0 || co2ePerMile <= 0) return 0;
    
    const minEmission = Math.min(...allValues.filter(v => v > 0));
    const maxEmission = Math.max(...allValues);
    
    if (maxEmission === minEmission) return 100; // All same value
    
    // Invert: lower emissions = higher score
    // Formula: score = 100 - ((value - min) / (max - min)) * 100
    const score = 100 - ((co2ePerMile - minEmission) / (maxEmission - minEmission)) * 100;
    return Math.max(0, Math.min(100, score));
  };

  // Get unique vehicle types from CSV with custom ordering
  const getVehicleTypes = () => {
    const types = new Set(vehicleLevelCSV.map(row => row.Vehicle_Type).filter(Boolean));
    const customOrder = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
    const typeArray = Array.from(types);
    
    return typeArray.sort((a, b) => {
      const findIndex = (vehicleType: string) => {
        const lowerType = vehicleType.toLowerCase();
        return customOrder.findIndex(order => order.toLowerCase() === lowerType);
      };
      
      const indexA = findIndex(a);
      const indexB = findIndex(b);
      
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  };

  // Component for CO2e (kg) label with info icon (both Logistics and ES modes)
  const CO2eLabel = ({ customTooltipText }: { customTooltipText?: string } = {}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    const isLogisticsMode = serviceType === 'logistics';
    const defaultTooltipText = isLogisticsMode
      ? "Total carbon emissions from driving and idling. For diesel trucks, this accounts for vehicle weight, speed, and idle time. For electric trucks, this is based on energy used and grid emissions."
      : "Total carbon emissions from driving and idling. Includes emissions from distance traveled (miles × carbon factor per mile) and engine idling time (idle hours × idle burn rate × carbon factor per litre).";
    
    const tooltipText = customTooltipText || defaultTooltipText;

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>CO2e (kg)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">CO2e (kg)</div>
            <div className="text-muted-foreground leading-relaxed">
              {tooltipText}
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Get columns for Individual Vehicle table - only: Trips/Day, Miles, CO2e, Fuel, Fuel Cost, Energy (Logistics), Elec. Cost (Logistics)
  const _getIndividualVehicleColumns = () => {
    const tripLabel = serviceType === 'emergency_services' ? 'Trips' : 'Trips/Day';
    const baseColumns = [
      { key: 'vehicle_id', label: 'Vehicle' },
      { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
      { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
      { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
      { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
      { key: 'active_fuel_litre', label: 'Fuel (L)', align: 'right' as const },
      { key: 'active_fuel_cost_GBP', label: 'Fuel Cost (£)', align: 'right' as const },
    ];

    // In Logistics mode, add Laden % and Energy/Elec. Cost columns
    const isLogisticsMode = serviceType === 'logistics';
    if (isLogisticsMode) {
      // Insert laden percentage after CO2e/mile to show loading efficiency context
      baseColumns.splice(4, 0, { key: 'avg_laden_percentage', label: 'Avg Laden %', align: 'right' as const });
      baseColumns.push(
        { key: 'energy_consumption_kWh', label: 'Energy (kWh)', align: 'right' as const },
        { key: 'electricity_cost_GBP', label: 'Elec. Cost (£)', align: 'right' as const },
      );
    }

    return baseColumns;
  };

  // Get columns for Scope 1 (Diesel) table - date before vehicle, then Vehicle Type; exclude Energy/Elec. Cost
  const getScope1VehicleColumns = () => {
    const tripLabel = serviceType === 'emergency_services' ? 'Trips' : 'Trips/Day';
    const baseColumns = [
      { key: 'date', label: 'Date' },
      { key: 'vehicle_id', label: 'Vehicle' },
      { key: 'vehicle_type', label: 'Vehicle Type' },
      { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
      { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
      { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
      { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
      { key: 'active_fuel_litre', label: 'Fuel (L)', align: 'right' as const },
      { key: 'active_fuel_cost_GBP', label: 'Fuel Cost (£)', align: 'right' as const },
    ];

    // In Logistics mode, add Laden % but NOT Energy/Elec. Cost
    const isLogisticsMode = serviceType === 'logistics';
    if (isLogisticsMode) {
      // Insert laden percentage after CO2e/mile (index 6: after date, vehicle, vehicle_type, trips, CO2e, co2e/mile)
      baseColumns.splice(6, 0, { key: 'avg_laden_percentage', label: 'Avg Laden %', align: 'right' as const });
    }

    return baseColumns;
  };

  // Get columns for Scope 2 (Electric) table - exclude Fuel/Fuel Cost
  const getScope2VehicleColumns = () => {
    const tripLabel = serviceType === 'emergency_services' ? 'Trips' : 'Trips/Day';
    const baseColumns =
      serviceType === 'emergency_services'
        ? [
            { key: 'date', label: 'Date' },
            { key: 'vehicle_id', label: 'Vehicle' },
            { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
            { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
            { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
            { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
          ]
        : [
            { key: 'vehicle_id', label: 'Vehicle' },
            { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
            { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
            { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
            { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
          ];

    // In Logistics mode, add Laden %, Energy, and Elec. Cost but NOT Fuel/Fuel Cost
    const isLogisticsMode = serviceType === 'logistics';
    if (isLogisticsMode) {
      // After CO2e/mile (logistics has no Date column in this table)
      const insertIdx = 4;
      baseColumns.splice(insertIdx, 0, { key: 'avg_laden_percentage', label: 'Avg Laden %', align: 'right' as const });
      baseColumns.push(
        { key: 'energy_consumption_kWh', label: 'Energy (kWh)', align: 'right' as const },
        { key: 'electricity_cost_GBP', label: 'Elec. Cost (£)', align: 'right' as const },
      );
    }

    return baseColumns;
  };

  // Get columns for Vehicle Type Summary table - only: Trips/Day, Miles, CO2e, Fuel, Fuel Cost, Energy (Logistics), Elec. Cost (Logistics)
  const getAggregatedVehicleColumns = () => {
    const tripLabel = serviceType === 'emergency_services' ? 'Trips' : 'Trips/Day';
    const baseColumns =
      serviceType === 'emergency_services'
        ? [
            { key: 'date', label: 'Date' },
            { key: 'vehicle_type', label: 'Type' },
            { key: 'vehicles_dispatched', label: 'Vehicles', align: 'right' as const },
            { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
            { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
            { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
            { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
            { key: 'active_fuel_litre', label: 'Fuel (L)', align: 'right' as const },
            { key: 'active_fuel_cost_GBP', label: 'Fuel Cost (£)', align: 'right' as const },
          ]
        : [
            { key: 'vehicle_type', label: 'Type' },
            { key: 'vehicles_dispatched', label: 'Vehicles', align: 'right' as const },
            { key: 'trips_per_day', label: tripLabel, align: 'right' as const },
            { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
            { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
            { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
            { key: 'active_fuel_litre', label: 'Fuel (L)', align: 'right' as const },
            { key: 'active_fuel_cost_GBP', label: 'Fuel Cost (£)', align: 'right' as const },
          ];

    // In Logistics mode, add Laden % and Energy/Elec. Cost columns
    const isLogisticsMode = serviceType === 'logistics';
    if (isLogisticsMode) {
      // Insert laden percentage after CO2e/mile. This branch only ever runs in
      // logistics mode (isLogisticsMode gates it), whose baseColumns has no
      // Date column, so the insert index is always 5.
      const ladenInsertIdx = 5;
      baseColumns.splice(ladenInsertIdx, 0, { key: 'avg_laden_percentage', label: 'Avg Laden %', align: 'right' as const });
      baseColumns.push(
        { key: 'energy_consumption_kWh', label: 'Energy (kWh)', align: 'right' as const },
        { key: 'electricity_cost_GBP', label: 'Elec. Cost (£)', align: 'right' as const },
      );
    }

    return baseColumns;
  };

  // Get columns for Station Summary table (Emergency Services only)
  const getStationSummaryColumns = () => {
    return [
      { key: 'date', label: 'Date' },
      { key: 'station_name', label: 'Station' },
      { key: 'vehicles_dispatched', label: 'Vehicles', align: 'right' as const },
      { key: 'trips_per_day', label: 'Trips', align: 'right' as const },
      { key: 'active_emission_kg', label: <CO2eLabel />, align: 'right' as const },
      { key: 'co2e_per_mile', label: 'CO2e (kg/mile)', align: 'right' as const },
      { key: 'co2e_per_trip', label: 'CO2e (kg/trip)', align: 'right' as const },
      { key: 'active_fuel_litre', label: 'Fuel (L)', align: 'right' as const },
      { key: 'active_fuel_cost_GBP', label: 'Fuel Cost (£)', align: 'right' as const },
    ];
  };

  // Get Scope 1 (diesel) vehicle data
  const getScope1VehicleData = () => {
    const vehicleTypeVal = (r: CSVRow) => r.Vehicle_Type || r.VehicleType || '';
    const filteredData = selectedVehicleType 
      ? vehicleLevelCSV.filter(csvRow => vehicleTypeVal(csvRow) === selectedVehicleType && !isElectricVehicle(vehicleTypeVal(csvRow)))
      : vehicleLevelCSV.filter(csvRow => !isElectricVehicle(vehicleTypeVal(csvRow)));
    return getDetailedVehicleDataFromFiltered(filteredData);
  };

  // Get Scope 2 (electric) vehicle data
  const getScope2VehicleData = () => {
    const vehicleTypeVal = (r: CSVRow) => r.Vehicle_Type || r.VehicleType || '';
    const filteredData = selectedVehicleType 
      ? vehicleLevelCSV.filter(csvRow => vehicleTypeVal(csvRow) === selectedVehicleType && isElectricVehicle(vehicleTypeVal(csvRow)))
      : vehicleLevelCSV.filter(csvRow => isElectricVehicle(vehicleTypeVal(csvRow)));
    return getDetailedVehicleDataFromFiltered(filteredData);
  };

  // Helper function to process detailed vehicle data from filtered list
  const getDetailedVehicleDataFromFiltered = (filteredData: CSVRow[]) => {
    
    // Calculate all CO2e/mile values first to determine min/max for scoring
    const allCo2ePerMileValues = filteredData.map(csvRow => {
      const emissions = getTotalEmissionsFromRow(csvRow);
      const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
      return miles > 0 && emissions > 0 ? emissions / miles : 0;
    }).filter(v => v > 0);
    
    return filteredData.map(csvRow => {
      const vehicleType = csvRow.VehicleType || csvRow.Vehicle_Type || '';
      
      const hasPrimaryData = isPrimaryData(csvRow);
      const isEstimated = !hasPrimaryData;
      const vehicleDisplay = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || '';
      const dateStr = (csvRow.date || csvRow.Date || '').toString().trim();
      
      return {
        date: dateStr || '—',
        vehicle_id: (
          <div className="leading-tight flex items-start gap-1.5">
            {hasPrimaryData && (
              <div
                className="relative"
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setShowVehiclePrimaryTooltip({
                    vehicleId: vehicleDisplay,
                    position: {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }
                  });
                }}
                onMouseLeave={() => {
                  setShowVehiclePrimaryTooltip(null);
                }}
              >
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0 drop-shadow-glow-success-sm cursor-help" />
              </div>
            )}
            {isEstimated && (
              <div
                className="relative"
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setShowVehicleEstimatedTooltip({
                    vehicleId: vehicleDisplay,
                    position: {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }
                  });
                }}
                onMouseLeave={() => {
                  setShowVehicleEstimatedTooltip(null);
                }}
              >
                <AlertTriangle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0 drop-shadow-glow-warning-sm cursor-help" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="whitespace-nowrap">{redactVehicleId(vehicleDisplay, isDemoMode)}</div>
            </div>
          </div>
        ),
        vehicle_type: redactVehicleType(vehicleType, isDemoMode) || '—',
        _vehicleId: csvRow.Vehicle_ID || csvRow.vehicle || '',
        vehicle_id_sortKey: vehicleDisplay.toLowerCase(),
        trips_per_day: (() => {
          // Try multiple possible column names for Logistics vs ES mode
          const trips = parseFloat(
            csvRow.num_trips || 
            csvRow.Num_Trips || 
            csvRow.trips || 
            csvRow.Trips || 
            '0'
          );
          if (serviceType === 'emergency_services') {
            if (trips <= 0) return '0';
            return Number.isInteger(trips) ? String(trips) : formatNumber(trips, 1);
          }
          const days = parseFloat(
            csvRow.total_days || 
            csvRow.Total_Days || 
            csvRow.days || 
            csvRow.Days || 
            '0'
          );
          // For individual vehicles, calculate trips per day directly
          if (days > 0 && trips > 0) {
            return formatNumber(trips / days, 1);
          }
          return '0.0';
        })(),
        total_miles: formatMiles(csvRow.total_miles || '0'),
        miles_per_day: (() => {
          // Try multiple possible column names for Logistics vs ES mode
          const miles = parseFloat(
            csvRow.total_miles || 
            csvRow.Total_Miles || 
            csvRow.miles || 
            csvRow.Miles || 
            '0'
          );
          const days = parseFloat(
            csvRow.total_days || 
            csvRow.Total_Days || 
            csvRow.days || 
            csvRow.Days || 
            '0'
          );
          // For individual vehicles, calculate miles per day directly
          if (days > 0 && miles > 0) {
            return formatMiles(miles / days);
          }
          return '0.0';
        })(),
        active_emission_kg: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const formatted = formatEmission(emissions.toString());
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          
          return (
            <EmissionFormulaTooltip
              csvRow={csvRow}
              serviceType={serviceType}
              vehicleType={vehicleType}
              totalMiles={miles}
              totalEmissions={emissions}
            >
              <span>{formatted}</span>
            </EmissionFormulaTooltip>
          );
        })(),
        active_emission_kg_sortKey: getTotalEmissionsFromRow(csvRow), // For sorting by numeric emission value
        co2e_per_mile: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          const formatted = co2ePerMile > 0 ? formatNumber(co2ePerMile, 2) : '0.00';
          return <span>{formatted}</span>;
        })(),
        co2e_per_mile_sortKey: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          return miles > 0 && emissions > 0 ? emissions / miles : 0;
        })(), // For sorting by numeric CO2e/mile value
        _co2ePerMileValue: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          return co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
        })(),
        _co2ePerMileColor: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          const score = co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
          return getScoreColor(score);
        })(),
        // Laden percentage (Logistics only) with color coding
        avg_laden_percentage: (() => {
          if (serviceType !== 'logistics') return null;
          const ladenValue = csvRow.avg_laden_percentage;
          
          // Check if value is "Unavailable"
          if (typeof ladenValue === 'string' && ladenValue.toLowerCase() === 'unavailable') {
            return (
              <span className="text-muted-foreground font-medium">
                Unavailable
              </span>
            );
          }
          
          const ladenPct = parseFloat(ladenValue || '0');
          if (isNaN(ladenPct)) return null;
          
          // Color coding: Green (70-100%), Yellow (40-70%), Red (<40%)
          let color = STATUS_COLORS.destructive; // red
          if (ladenPct >= 70) color = STATUS_COLORS.success; // green
          else if (ladenPct >= 40) color = STATUS_COLORS.warning; // yellow
          
          return (
            <span style={{ color }} className="font-medium">
              {ladenPct.toFixed(1)}%
            </span>
          );
        })(),
        avg_laden_percentage_sortKey: (() => {
          if (serviceType !== 'logistics') return null;
          const ladenValue = csvRow.avg_laden_percentage;
          // Return -1 for "Unavailable" so it sorts to the end when descending, or to the beginning when ascending
          if (typeof ladenValue === 'string' && ladenValue.toLowerCase() === 'unavailable') {
            return -1;
          }
          const ladenPct = parseFloat(ladenValue || '0');
          return isNaN(ladenPct) ? null : ladenPct;
        })(), // For sorting by numeric laden percentage value
        _avgLadenPercentageValue: serviceType === 'logistics' ? (
          typeof csvRow.avg_laden_percentage === 'string' && csvRow.avg_laden_percentage.toLowerCase() === 'unavailable'
            ? null
            : parseFloat(csvRow.avg_laden_percentage || '0')
        ) : null,
        co2e_per_trip: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const trips = parseFloat(
            csvRow.num_trips || 
            csvRow.Num_Trips || 
            csvRow.trips || 
            csvRow.Trips || 
            '0'
          );
          if (trips > 0 && emissions > 0) {
            return formatEmission(emissions / trips);
          }
          return '0.0';
        })(),
        active_fuel_litre: formatFuel(csvRow.active_fuel_litre || '0'),
        active_fuel_cost_GBP: formatCurrency(csvRow.active_fuel_cost_GBP || '0'),
        energy_consumption_kWh: formatNumber(csvRow.energy_consumption_kWh || '0', 1),
        electricity_cost_GBP: formatCurrency(csvRow.electricity_cost_GBP || '0'),
      };
    });
  };

  // Process station-level carbon data (Emergency Services only)
  const getStationSummaryData = () => {
    if (serviceType !== 'emergency_services' || stationLevelCSV.length === 0) return [];

    // Calculate all CO2e/mile values first to determine min/max for scoring
    const allCo2ePerMileValues = stationLevelCSV.map(csvRow => {
      const emissions = getTotalEmissionsFromRow(csvRow);
      const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
      return miles > 0 && emissions > 0 ? emissions / miles : 0;
    }).filter(v => v > 0);

    return stationLevelCSV.map(csvRow => {
      const stationName = csvRow.Station_Name || csvRow.Station || csvRow['STN #'] || csvRow.StationName || '';
      
      // Trips: ES station rows are daily — show trip count; logistics-style uses trips per vehicle-day when derivable
      const calculateTripsPerDay = () => {
        const trips = parseFloat(
          csvRow.num_trips || 
          csvRow.Num_Trips || 
          csvRow.trips || 
          csvRow.Trips || 
          '0'
        );
        if (serviceType === 'emergency_services') {
          if (trips <= 0) return '0';
          return Number.isInteger(trips) ? String(trips) : formatNumber(trips, 1);
        }
        
        const vehicles = parseFloat(
          csvRow.vehicles_dispatched || 
          csvRow.vehicle_dispatched ||
          csvRow.Vehicles_Dispatched || 
          csvRow.Vehicle_Dispatched ||
          csvRow.vehicles || 
          csvRow.Vehicles || 
          '0'
        );
        
        // Station level CSV doesn't have total_days, so calculate from vehicle level CSV
        if (vehicleLevelCSV.length > 0 && stationName && trips > 0) {
          const vehiclesAtStation = vehicleLevelCSV.filter(row => {
            const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
            return rowStation.toLowerCase() === stationName.toLowerCase();
          });
          
          if (vehiclesAtStation.length > 0) {
            const totalDays = vehiclesAtStation.reduce((sum, row) => {
              return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
            }, 0);
            
            const avgDays = totalDays / vehiclesAtStation.length;
            // Use vehicle count from CSV if available, otherwise use count from vehicle-level CSV
            const vehicleCount = vehicles > 0 ? vehicles : vehiclesAtStation.length;
            
            if (avgDays > 0 && trips > 0 && vehicleCount > 0) {
              return formatNumber((trips / vehicleCount) / avgDays, 1);
            }
          }
        }
        
        return '0.0';
      };
      
      // Get vehicle count - try from station-level CSV first, then fallback to counting from vehicle-level CSV
      const getVehicleCount = () => {
        const vehiclesFromCSV = parseFloat(
          csvRow.vehicles_dispatched || 
          csvRow.vehicle_dispatched ||
          csvRow.Vehicles_Dispatched || 
          csvRow.Vehicle_Dispatched ||
          csvRow.vehicles || 
          csvRow.Vehicles || 
          '0'
        );
        
        // If station-level CSV has vehicle count, use it
        if (vehiclesFromCSV > 0) {
          return vehiclesFromCSV;
        }
        
        // Fallback: count vehicles from vehicle-level CSV
        if (vehicleLevelCSV.length > 0 && stationName) {
          const vehiclesAtStation = vehicleLevelCSV.filter(row => {
            const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
            return rowStation.toLowerCase() === stationName.toLowerCase();
          });
          return vehiclesAtStation.length;
        }
        
        return 0;
      };
      
      const vehicleCount = getVehicleCount();
      const rowDateRaw = (csvRow.date || csvRow.Date || csvRow.day || csvRow.Day || '').toString().trim();
      const rowDateIso = rowDateRaw.length >= 10 ? rowDateRaw.slice(0, 10) : rowDateRaw;
      
      return {
        date: rowDateIso || '—',
        _date_sortKey: rowDateIso,
        station_name: redactStationName(stationName, isDemoMode),
        _station_sortKey: stationName, // For sorting/export (keep original for internal use)
        vehicles_dispatched: formatCount(vehicleCount.toString()),
        trips_per_day: calculateTripsPerDay(),
        total_miles: formatMiles(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0'),
        miles_per_day: (() => {
          const miles = parseFloat(
            csvRow.total_miles || 
            csvRow.Total_Miles || 
            csvRow.miles || 
            csvRow.Miles || 
            '0'
          );
          
          if (vehicleLevelCSV.length > 0 && stationName && miles > 0) {
            const vehiclesAtStation = vehicleLevelCSV.filter(row => {
              const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
              return rowStation.toLowerCase() === stationName.toLowerCase();
            });
            
            if (vehiclesAtStation.length > 0) {
              const totalDays = vehiclesAtStation.reduce((sum, row) => {
                return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
              }, 0);
              
              const avgDays = totalDays / vehiclesAtStation.length;
              // Use vehicle count from outer scope if available, otherwise use count from vehicle-level CSV
              const countForMiles = vehicleCount > 0 ? vehicleCount : vehiclesAtStation.length;
              
              if (avgDays > 0 && miles > 0 && countForMiles > 0) {
                return formatMiles((miles / countForMiles) / avgDays);
              }
            }
          }
          
          return '0.0';
        })(),
        active_emission_kg: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const formatted = formatEmission(emissions.toString());
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          
          return (
            <EmissionFormulaTooltip
              csvRow={csvRow}
              serviceType={serviceType}
              totalMiles={miles}
              totalEmissions={emissions}
            >
              <span>{formatted}</span>
            </EmissionFormulaTooltip>
          );
        })(),
        active_emission_kg_sortKey: getTotalEmissionsFromRow(csvRow), // For sorting by numeric emission value
        co2e_per_mile: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          if (miles > 0 && emissions > 0) {
            return formatNumber(emissions / miles, 2);
          }
          return '0.00';
        })(),
        co2e_per_mile_sortKey: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          return miles > 0 && emissions > 0 ? emissions / miles : 0;
        })(), // For sorting by numeric CO2e/mile value
        _co2ePerMileValue: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          return co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
        })(),
        _co2ePerMileColor: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          const score = co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
          return getScoreColor(score);
        })(),
        co2e_per_trip: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const trips = parseFloat(
            csvRow.num_trips || 
            csvRow.Num_Trips || 
            csvRow.trips || 
            csvRow.Trips || 
            '0'
          );
          if (trips > 0 && emissions > 0) {
            return formatEmission(emissions / trips);
          }
          return '0.0';
        })(),
        active_fuel_litre: formatFuel(csvRow.active_fuel_litre || '0'),
        active_fuel_cost_GBP: formatCurrency(csvRow.active_fuel_cost_GBP || '0'),
      };
    }).sort((a, b) => {
      const da = (a as { _date_sortKey?: string })._date_sortKey || '';
      const db = (b as { _date_sortKey?: string })._date_sortKey || '';
      const c = da.localeCompare(db);
      if (c !== 0) return c;
      return a._station_sortKey.localeCompare(b._station_sortKey);
    });
  };

  // Get Scope 1 aggregated vehicle data (diesel only)
  const getScope1AggregatedData = () => {
    const scope1Types = vehicleTypeLevelCSV.filter(row => !isElectricVehicle(row.Vehicle_Type || ''));
    return getAggregatedVehicleDataFromFiltered(scope1Types);
  };

  // Get Scope 2 aggregated vehicle data (electric only)
  const getScope2AggregatedData = () => {
    const scope2Types = vehicleTypeLevelCSV.filter(row => isElectricVehicle(row.Vehicle_Type || ''));
    return getAggregatedVehicleDataFromFiltered(scope2Types);
  };

  // Helper function to process aggregated vehicle data from filtered list
  const getAggregatedVehicleDataFromFiltered = (filteredCSV: CSVRow[]) => {
    // Calculate all CO2e/mile values first to determine min/max for scoring
    const allCo2ePerMileValues = filteredCSV.map(csvRow => {
      const emissions = getTotalEmissionsFromRow(csvRow);
      const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
      return miles > 0 && emissions > 0 ? emissions / miles : 0;
    }).filter(v => v > 0);
    
    const mappedRows = filteredCSV.map(csvRow => {
      const rowDateRaw = (csvRow.date || csvRow.Date || csvRow.day || csvRow.Day || '').toString().trim();
      const rowDateIso = rowDateRaw.length >= 10 ? rowDateRaw.slice(0, 10) : rowDateRaw;
      const vehicleType = csvRow.Vehicle_Type || '';
      const isSelectedType = selectedVehicleType && 
        vehicleType.toLowerCase() === selectedVehicleType.toLowerCase();
      
      // Calculate trips_per_day - vehicle type CSV doesn't have total_days, so calculate from vehicle level CSV
      const calculateTripsPerDay = () => {
        // Get trips from vehicle type CSV (using correct field name: num_trips)
        const trips = parseFloat(
          csvRow.num_trips || 
          csvRow.Num_Trips || 
          csvRow.trips || 
          csvRow.Trips || 
          '0'
        );
        if (serviceType === 'emergency_services') {
          if (trips <= 0) return '0';
          return Number.isInteger(trips) ? String(trips) : formatNumber(trips, 1);
        }
        
        // Get vehicle count (check both singular and plural forms)
        const vehicles = parseFloat(
          csvRow.vehicles_dispatched || 
          csvRow.vehicle_dispatched ||
          csvRow.Vehicles_Dispatched || 
          csvRow.Vehicle_Dispatched ||
          csvRow.vehicles || 
          csvRow.Vehicles || 
          '0'
        );
        
        // Vehicle type CSV doesn't have total_days, so always calculate from vehicle level CSV
        if (vehicleLevelCSV.length > 0 && vehicleType && trips > 0) {
          const vehiclesOfType = vehicleLevelCSV.filter(row => 
            (row.Vehicle_Type || row.vehicle_type || '').toLowerCase() === vehicleType.toLowerCase()
          );
          
          if (vehiclesOfType.length > 0) {
            // Calculate average days per vehicle from vehicle level CSV
            const totalDays = vehiclesOfType.reduce((sum, row) => {
              return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
            }, 0);
            
            const avgDays = totalDays / vehiclesOfType.length;
            
            // Use vehicle count from vehicle type CSV, or fall back to count from vehicle level CSV
            const vehicleCount = vehicles > 0 ? vehicles : vehiclesOfType.length;
            
            if (avgDays > 0 && trips > 0 && vehicleCount > 0) {
              // Calculate: (total trips / number of vehicles) / average days per vehicle
              return formatNumber((trips / vehicleCount) / avgDays, 1);
            }
          }
        }
        
        return '0.0';
      };
      
      return {
        date: rowDateIso || '—',
        _date_sortKey: rowDateIso,
        vehicle_id: (
          <span className="font-semibold">
            {redactVehicleType(vehicleType, isDemoMode)}
          </span>
        ),
        vehicle_type: (
          <span className="font-semibold">
            {redactVehicleType(vehicleType, isDemoMode)}
          </span>
        ),
        _vehicleId: `TOTAL_${vehicleType}`,
        _vehicleType: vehicleType,
        _vehicle_type_sortKey: vehicleType,
        vehicle_id_sortKey: vehicleType.toLowerCase(), // For sorting by vehicle type
        _isStickyRow: serviceType === 'logistics', // Make aggregated rows sticky in Logistics mode
        vehicles_dispatched: formatCount(csvRow.vehicles_dispatched || csvRow.vehicle_dispatched || '0'),
        trips_per_day: calculateTripsPerDay(),
        total_miles: formatMiles(csvRow.total_miles || '0'),
        miles_per_day: (() => {
          // Get miles from vehicle type CSV
          const miles = parseFloat(
            csvRow.total_miles || 
            csvRow.Total_Miles || 
            csvRow.miles || 
            csvRow.Miles || 
            '0'
          );
          
          // Get vehicle count (check both singular and plural forms)
          const vehicles = parseFloat(
            csvRow.vehicles_dispatched || 
            csvRow.vehicle_dispatched ||
            csvRow.Vehicles_Dispatched || 
            csvRow.Vehicle_Dispatched ||
            csvRow.vehicles || 
            csvRow.Vehicles || 
            '0'
          );
          
          // Vehicle type CSV doesn't have total_days, so calculate from vehicle level CSV
          if (vehicleLevelCSV.length > 0 && vehicleType && miles > 0) {
            const vehiclesOfType = vehicleLevelCSV.filter(row => 
              (row.Vehicle_Type || row.vehicle_type || '').toLowerCase() === vehicleType.toLowerCase()
            );
            
            if (vehiclesOfType.length > 0) {
              // Calculate average days per vehicle from vehicle level CSV
              const totalDays = vehiclesOfType.reduce((sum, row) => {
                return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
              }, 0);
              
              const avgDays = totalDays / vehiclesOfType.length;
              
              // Use vehicle count from vehicle type CSV, or fall back to count from vehicle level CSV
              const vehicleCount = vehicles > 0 ? vehicles : vehiclesOfType.length;
              
              if (avgDays > 0 && miles > 0 && vehicleCount > 0) {
                // Calculate: (total miles / number of vehicles) / average days per vehicle
                return formatMiles((miles / vehicleCount) / avgDays);
              }
            }
          }
          
          return '0.0';
        })(),
      active_emission_kg: (() => {
        const emissions = getTotalEmissionsFromRow(csvRow);
        const formatted = formatEmission(emissions.toString());
        const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
        
        return (
          <EmissionFormulaTooltip
            csvRow={csvRow}
            serviceType={serviceType}
            vehicleType={vehicleType}
            totalMiles={miles}
            totalEmissions={emissions}
          >
            <span>{formatted}</span>
          </EmissionFormulaTooltip>
        );
      })(),
      active_emission_kg_sortKey: getTotalEmissionsFromRow(csvRow), // For sorting by numeric emission value
        co2e_per_mile: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
        const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
        const formatted = co2ePerMile > 0 ? formatNumber(co2ePerMile, 2) : '0.00';
        return <span>{formatted}</span>;
        })(),
      co2e_per_mile_sortKey: (() => {
        const emissions = getTotalEmissionsFromRow(csvRow);
        const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
        return miles > 0 && emissions > 0 ? emissions / miles : 0;
      })(), // For sorting by numeric CO2e/mile value
        _co2ePerMileValue: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          return co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
        })(),
        _co2ePerMileColor: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
          const co2ePerMile = miles > 0 && emissions > 0 ? emissions / miles : 0;
          const score = co2ePerMile > 0 ? getCo2ePerMileScore(co2ePerMile, allCo2ePerMileValues) : 0;
          return getScoreColor(score);
        })(),
        co2e_per_trip: (() => {
          const emissions = getTotalEmissionsFromRow(csvRow);
          const trips = parseFloat(
            csvRow.num_trips || 
            csvRow.Num_Trips || 
            csvRow.trips || 
            csvRow.Trips || 
            '0'
          );
          if (trips > 0 && emissions > 0) {
            return formatEmission(emissions / trips);
          }
          return '0.0';
        })(),
        active_fuel_litre: formatFuel(csvRow.active_fuel_litre || '0'),
        active_fuel_cost_GBP: formatCurrency(csvRow.active_fuel_cost_GBP || '0'),
        energy_consumption_kWh: formatNumber(csvRow.energy_consumption_kWh || '0', 1),
        electricity_cost_GBP: formatCurrency(csvRow.electricity_cost_GBP || '0'),
        // Laden percentage (Logistics only) with color coding
        avg_laden_percentage: (() => {
          if (serviceType !== 'logistics') return null;
          const ladenValue = csvRow.avg_laden_percentage;
          
          // Check if value is "Unavailable"
          if (typeof ladenValue === 'string' && ladenValue.toLowerCase() === 'unavailable') {
            return (
              <span className="text-muted-foreground font-medium">
                Unavailable
              </span>
            );
          }
          
          const ladenPct = parseFloat(ladenValue || '0');
          if (isNaN(ladenPct)) return null;
          
          // Color coding: Green (70-100%), Yellow (40-70%), Red (<40%)
          let color = STATUS_COLORS.destructive; // red
          if (ladenPct >= 70) color = STATUS_COLORS.success; // green
          else if (ladenPct >= 40) color = STATUS_COLORS.warning; // yellow
          
          return (
            <span style={{ color }} className="font-medium">
              {ladenPct.toFixed(1)}%
            </span>
          );
        })(),
        avg_laden_percentage_sortKey: (() => {
          if (serviceType !== 'logistics') return null;
          const ladenValue = csvRow.avg_laden_percentage;
          // Return -1 for "Unavailable" so it sorts to the end when descending, or to the beginning when ascending
          if (typeof ladenValue === 'string' && ladenValue.toLowerCase() === 'unavailable') {
            return -1;
          }
          const ladenPct = parseFloat(ladenValue || '0');
          return isNaN(ladenPct) ? null : ladenPct;
        })(), // For sorting by numeric laden percentage value
        _avgLadenPercentageValue: serviceType === 'logistics' ? (
          typeof csvRow.avg_laden_percentage === 'string' && csvRow.avg_laden_percentage.toLowerCase() === 'unavailable'
            ? null
            : parseFloat(csvRow.avg_laden_percentage || '0')
        ) : null,
        _isHighlighted: isSelectedType,
      };
    });
    if (serviceType !== 'emergency_services') return mappedRows;
    return [...mappedRows].sort((a, b) => {
      const ra = a as Record<string, unknown>;
      const rb = b as Record<string, unknown>;
      const da = (ra._date_sortKey as string) || '';
      const db = (rb._date_sortKey as string) || '';
      const cmp = da.localeCompare(db);
      if (cmp !== 0) return cmp;
      const ta = (ra._vehicle_type_sortKey as string) || '';
      const tb = (rb._vehicle_type_sortKey as string) || '';
      return ta.localeCompare(tb);
    });
  };

  // Get unique vehicles from vehicleLevelCSV for dropdown
  const getAvailableVehicles = () => {
    const vehicles = new Set<{ id: string; type: string }>();
    vehicleLevelCSV.forEach(row => {
      const vehicleId = row.Vehicle_ID || row.VehicleID || row.vehicle_id || '';
      const vehicleType = row.Vehicle_Type || row.vehicle_type || '';
      if (vehicleId) {
        vehicles.add({ id: vehicleId, type: vehicleType });
      }
    });
    return Array.from(vehicles).sort((a, b) => a.id.localeCompare(b.id));
  };

  // Handle manual carbon form submission
  const handleCarbonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingCarbon(true);
    setCarbonSubmitSuccess(false);
    setCarbonSubmitError(null);

    try {
      const { API_ENDPOINTS } = await import('../config/api');
      
      // Prepare form data - if "__NEW_VEHICLE__" is selected, use the new vehicle ID and type
      const formData = {
        ...manualCarbonForm,
        vehicle_id: manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' ? newVehicleIdCarbon : manualCarbonForm.vehicle_id,
        vehicle_type: manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' && newVehicleTypeCarbon ? newVehicleTypeCarbon : manualCarbonForm.vehicle_type
      };
      
      // Validate that new vehicle ID is provided if "__NEW_VEHICLE__" is selected
      if (manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' && !newVehicleIdCarbon.trim()) {
        setCarbonSubmitError('Please enter a vehicle ID for the new vehicle');
        setSubmittingCarbon(false);
        return;
      }
      
      // Validate that vehicle type is provided if adding a new vehicle
      if (manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' && !formData.vehicle_type.trim()) {
        setCarbonSubmitError('Please select or enter a vehicle type for the new vehicle');
        setSubmittingCarbon(false);
        return;
      }
      
      const response = await authenticatedFetch(API_ENDPOINTS.manualCarbonSubmit(serviceType), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit fuel/energy entry');
      }

      await response.json();
      setCarbonSubmitSuccess(true);
      setCarbonSubmitError(null);
      
      // Reset form
      setManualCarbonForm({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        vehicle_id: '',
        vehicle_type: '',
        fuel_type: 'Diesel',
        quantity: '',
        cost: '',
        odometer: '',
        notes: ''
      });
      setNewVehicleIdCarbon('');
      setNewVehicleTypeCarbon('');
      setShowNewVehicleTypeInputCarbon(false);
      setSelectedCarbonEntries(new Set()); // Clear selected entries

      // Hide success message after 3 seconds and close modal
      setTimeout(() => {
        setCarbonSubmitSuccess(false);
        setShowManualCarbonForm(false);
      }, 3000);
      
      // Refresh manual entries after a short delay to ensure backend has written the data
      console.log('[Manual Carbon] Submission successful, scheduling fetch in 1000ms...');
      logger.debug('Scheduling fetch of manual carbon entries in 1000ms...');
      setTimeout(() => {
        console.log('[Manual Carbon] Fetching entries after submission...');
        logger.debug('Fetching manual carbon entries after submission...');
        fetchManualCarbonEntries();
      }, 1000);
    } catch (error: any) {
      setCarbonSubmitError(error.message || 'Failed to submit fuel/energy entry');
      setCarbonSubmitSuccess(false);
    } finally {
      setSubmittingCarbon(false);
    }
  };

  // Fetch manual carbon entries
  const fetchManualCarbonEntries = useCallback(async () => {
    try {
      const { API_ENDPOINTS } = await import('../config/api');
      const url = API_ENDPOINTS.manualCarbonGet(serviceType);
      console.log('[Manual Carbon] Fetching from URL:', url);
      logger.debug('Fetching manual carbon entries from:', url);
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const entries = await response.json();
        console.log('[Manual Carbon] Response status:', response.status);
        console.log('[Manual Carbon] Fetched entries (raw):', entries);
        console.log('[Manual Carbon] Number of entries:', entries?.length || 0);
        logger.debug('Fetched manual carbon entries:', entries);
        logger.debug('Number of entries:', entries?.length || 0);
        if (Array.isArray(entries)) {
          // Log first entry structure for debugging
          if (entries.length > 0) {
            console.log('[Manual Carbon] First entry structure:', entries[0]);
            console.log('[Manual Carbon] First entry keys:', Object.keys(entries[0]));
            console.log('[Manual Carbon] First entry values:', Object.values(entries[0]));
            logger.debug('First entry structure:', entries[0]);
            logger.debug('First entry keys:', Object.keys(entries[0]));
          }
          console.log('[Manual Carbon] Setting entries, count:', entries.length);
          setManualCarbonEntries(entries);
          logger.debug('Set manual carbon entries state:', entries.length);
        } else {
          logger.warn('Response is not an array:', entries);
          setManualCarbonEntries([]);
        }
      } else {
        const errorText = await response.text();
        logger.warn('Failed to fetch manual carbon entries:', response.status, response.statusText, errorText);
        setManualCarbonEntries([]);
      }
    } catch (error) {
      logger.error('Error fetching manual carbon entries:', error);
      setManualCarbonEntries([]);
    }
  }, [serviceType]);

  // Load manual entries when component mounts or serviceType changes
  useEffect(() => {
    fetchManualCarbonEntries();
  }, [fetchManualCarbonEntries]);

  // Handle checkbox selection
  const handleCheckboxChangeCarbon = (entryIndex: number) => {
    setSelectedCarbonEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryIndex)) {
        newSet.delete(entryIndex);
      } else {
        newSet.add(entryIndex);
      }
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAllCarbon = (checked: boolean) => {
    if (checked) {
      setSelectedCarbonEntries(new Set(manualCarbonEntries.map((_, idx) => idx)));
    } else {
      setSelectedCarbonEntries(new Set());
    }
  };

  // Handle delete selected entries
  const handleDeleteSelectedCarbon = async () => {
    if (selectedCarbonEntries.size === 0) return;
    
    const indicesToDelete = Array.from(selectedCarbonEntries).sort((a, b) => b - a); // Sort descending to delete from end
    setDeleteCarbonConfirm(indicesToDelete);
  };

  // Confirm and delete entries
  const confirmDeleteCarbonEntries = async () => {
    if (!deleteCarbonConfirm || deleteCarbonConfirm.length === 0) return;

    try {
      const { API_ENDPOINTS } = await import('../config/api');
      
      // Delete entries from highest index to lowest to maintain correct indices
      for (const entryIndex of deleteCarbonConfirm.sort((a, b) => b - a)) {
        const response = await authenticatedFetch(API_ENDPOINTS.manualCarbonDelete(serviceType), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ entryIndex }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete entry');
        }
      }

      // Refresh entries and clear selection
      await fetchManualCarbonEntries();
      setSelectedCarbonEntries(new Set());
      setDeleteCarbonConfirm(null);
    } catch (error: any) {
      logger.error('Error deleting entries:', error);
      alert(`Failed to delete entries: ${error.message}`);
    }
  };

  // Get display value for selected vehicle (just the ID, no type)
  const getSelectedVehicleDisplayCarbon = () => {
    if (manualCarbonForm.vehicle_id === '__NEW_VEHICLE__') {
      return newVehicleIdCarbon || '';
    } else if (manualCarbonForm.vehicle_id) {
      const vehicle = getAvailableVehicles().find(v => v.id === manualCarbonForm.vehicle_id);
      return vehicle ? redactVehicleId(vehicle.id, isDemoMode) : '';
    }
    return '';
  };

  // Update vehicle type when vehicle is selected
  const handleVehicleSelectCarbon = (vehicleId: string) => {
    if (vehicleId === '__NEW_VEHICLE__') {
      // "Add a new vehicle" selected - set flag and clear new vehicle ID
      setManualCarbonForm({
        ...manualCarbonForm,
        vehicle_id: '__NEW_VEHICLE__',
        vehicle_type: '' // Clear type when adding new vehicle
      });
      setNewVehicleIdCarbon(''); // Clear the new vehicle ID input
      setNewVehicleTypeCarbon(''); // Clear new vehicle type
      setShowNewVehicleTypeInputCarbon(false); // Reset new vehicle type input visibility
    } else {
      const vehicle = getAvailableVehicles().find(v => v.id === vehicleId);
      setManualCarbonForm({
        ...manualCarbonForm,
        vehicle_id: vehicleId,
        vehicle_type: vehicle?.type || '' // Auto-populate from selected vehicle
      });
      setNewVehicleIdCarbon(''); // Clear new vehicle ID when selecting existing vehicle
      setNewVehicleTypeCarbon(''); // Clear new vehicle type
      setShowNewVehicleTypeInputCarbon(false); // Reset new vehicle type input visibility
    }
  };

  // ✅ Show message only if no period is selected (meaning no data available from backend)
  // Allow data fetching to proceed even if isDataReady is false - backend may have data
  if (!selectedPeriod && !loading) {
    return (
      <div className="flex flex-col items-center justify-center h-72 rounded-2xl border border-border bg-card/50 text-center p-8 space-y-3">
        <Upload className="w-10 h-10 text-muted-foreground" />
        <h2 className="text-xl font-semibold text-white">Demo Mode - No Data Available</h2>
        <p className="text-muted-foreground">This is a demo view. Upload telematics and fleet data in the Data Upload tab to view actual carbon insights.</p>
      </div>
    );
  }

  // Show subtabs and handle no period case (in this branch activeSection is 'fleet')
  if (!selectedPeriod && activeSection === 'fleet') {
    return (
      <div className="space-y-8 overflow-visible">
        {/* Subtabs Navigation */}
        <div className="bg-card rounded-lg border border-border p-2 flex gap-2">
          <button
            onClick={() => setActiveSection('fleet')}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition bg-primary text-white"
          >
            <Truck className="w-4 h-4 inline mr-2" />
            Fleet Carbon Reporting
          </button>
          <button
            onClick={() => setActiveSection('client')}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition text-muted-foreground hover:bg-secondary"
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Client Carbon Reporting
          </button>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Fleet Carbon Reporting</h2>
            <p className="text-muted-foreground text-sm">
              Track carbon emissions and fuel costs to support sustainability goals, budget planning, and identify opportunities for fleet electrification.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <PeriodSelector />
          </div>
        </div>
        <div className="text-center py-12 bg-card/30 rounded-lg border border-border">
          <p className="text-muted-foreground">Please select a period to view carbon reporting data.</p>
        </div>
      </div>
    );
  }

  // Safely get data quality metrics with error handling
  let qualityMetrics;
  try {
    qualityMetrics = getDataQualityMetrics();
  } catch (error) {
    logger.error('Error calculating data quality metrics:', error);
    qualityMetrics = { primaryData: 0, estimatedData: 0, coverage: 0 };
  }

  // Safely run benchmark comparison (errors logged; result reserved for future UI)
  try {
    void getFleetBenchmarkComparison();
  } catch (error) {
    logger.error('Error calculating benchmark comparison:', error);
  }

  // Reserved for future UI (PDF export, week nav, etc.) – reference so TS does not report unused
  void [
    _getTotalEmissions,
    _generatePDFReport,
    _hasPreviousWeek,
    _hasNextWeek,
    _getPreviousWeekLabel,
    _getNextWeekLabel,
    _getEmissionsData,
    _getFuelCostsSeries,
    _getIndividualVehicleColumns
  ];

  return (
    <div className="w-full min-w-0 space-y-8 overflow-visible">
      {/* Subtabs Navigation */}
      <div className="bg-card rounded-lg border border-border p-2 flex gap-0">
        <button
          onClick={() => setActiveSection('fleet')}
          className={`flex-1 px-4 py-2 rounded-l-lg rounded-r-none text-sm font-medium transition border-r border-border ${
            activeSection === 'fleet'
              ? 'bg-primary text-white'
              : 'text-muted-foreground hover:bg-secondary'
          }`}
        >
          <Truck className="w-4 h-4 inline mr-2" />
          Fleet Carbon Reporting
        </button>
        <button
          onClick={() => setActiveSection('client')}
          className={`flex-1 px-4 py-2 rounded-r-lg rounded-l-none text-sm font-medium transition ${
            activeSection === 'client'
              ? 'bg-primary text-white'
              : 'text-muted-foreground hover:bg-secondary'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Client Carbon Reporting
        </button>
      </div>

      {/* Fleet Carbon Reporting Section */}
      {activeSection === 'fleet' && (
        <>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
              <h2 className="text-2xl font-bold text-white mb-2">Fleet Carbon Reporting</h2>
          <p className="text-muted-foreground text-sm">
            Track carbon emissions and fuel costs to support sustainability goals, budget planning, and identify opportunities for fleet electrification.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <PeriodSelector />
        </div>
      </div>


      {/* Tooltips for Data Quality labels */}
      {showPrimaryDataTooltip && primaryDataTooltipPosition && createPortal(
        <div
          className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${primaryDataTooltipPosition.x}px`,
            top: `${primaryDataTooltipPosition.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '280px'
          }}
        >
          <div className="font-semibold mb-1.5 text-success drop-shadow-glow-success-lg">Primary Data</div>
          <div className="text-muted-foreground leading-relaxed">
            Data derived directly from telematics sensors, including GPS, speed, weight, and altitude measurements. This data includes correction factors (speed, gradient, laden percentage) applied to emissions calculations, providing the most accurate and audit-ready results.
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
        </div>,
        document.body
      )}

      {showEstimatedTooltip && estimatedTooltipPosition && createPortal(
        <div
          className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${estimatedTooltipPosition.x}px`,
            top: `${estimatedTooltipPosition.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '280px'
          }}
        >
          <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Estimated</div>
          <div className="text-muted-foreground leading-relaxed">
            Data calculated using fallback methods when primary telematics data is unavailable or incomplete. These estimates use default values and simplified calculations, which may result in less precise emissions figures compared to primary data.
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
        </div>,
        document.body
      )}

      {/* Tooltips for vehicle icon tooltips */}
      {showVehiclePrimaryTooltip && createPortal(
        <div
          className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${showVehiclePrimaryTooltip.position.x}px`,
            top: `${showVehiclePrimaryTooltip.position.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '200px'
          }}
        >
          <div className="font-semibold text-success drop-shadow-glow-success-lg">Primary Data</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
        </div>,
        document.body
      )}

      {showVehicleEstimatedTooltip && createPortal(
        <div
          className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${showVehicleEstimatedTooltip.position.x}px`,
            top: `${showVehicleEstimatedTooltip.position.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '200px'
          }}
        >
          <div className="font-semibold text-warning drop-shadow-glow-warning-lg">Estimated</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
        </div>,
        document.body
      )}


      {/* Methodology Documentation Modal */}
      {showMethodologyModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => setShowMethodologyModal(false)}
        >
          <div
            className="bg-card rounded-lg shadow-xl border border-border max-w-3xl w-full max-h-screen-90 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Methodology Documentation</h2>
              <button
                onClick={() => setShowMethodologyModal(false)}
                className="text-muted-foreground hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">GLEC Framework Alignment</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Our carbon emissions calculations are aligned with the Global Logistics Emissions Council (GLEC) Framework, 
                  the recognised global standard for calculating and reporting logistics emissions. The GLEC Framework provides 
                  a consistent methodology for quantifying greenhouse gas emissions across different transport modes and supply chains.
                </p>
                <a
                  href="https://www.smartfreightcentre.org/en/what-we-do/our-work/global-logistics-emissions-council-framework/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-warning hover:text-warning hover:underline text-sm mt-2 inline-flex items-center gap-1"
                >
                  Learn more about GLEC Framework
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">ISO 14083 Compliance</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Our methodology complies with ISO 14083:2023, the international standard for quantifying and reporting 
                  transport-related greenhouse gas emissions. This standard ensures consistency, accuracy, and comparability 
                  of emissions data across different organisations and reporting periods.
                </p>
                <a
                  href="https://www.iso.org/standard/78818.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-warning hover:text-warning hover:underline text-sm mt-2 inline-flex items-center gap-1"
                >
                  Learn more about ISO 14083
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Emission Factors & Data Sources</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground font-medium mb-1">Diesel Vehicle Emissions:</p>
                    <p className="text-muted-foreground">
                      <strong className="text-muted-foreground">Source:</strong> UK Government GHG Conversion Factors 2025
                    </p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Base emission factors are interpolated based on vehicle laden percentage (0% to 100% laden) 
                      and adjusted for speed and gradient corrections derived from telematics data.
                    </p>
                  </div>
                  
                  {serviceType === 'logistics' && (
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Electric Vehicle Emissions:</p>
                      <p className="text-muted-foreground">
                        <strong className="text-muted-foreground">Source:</strong> UK Government GHG Conversion Factors 2025
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">
                        Grid emission factor: 0.177 kg CO₂e/kWh (UK average grid mix, 2025)
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-muted-foreground font-medium mb-1">Scope 3 (Well-to-Tank) Emissions:</p>
                    <p className="text-muted-foreground">
                      Calculated from order-level data using upstream emission factors for fuel production, 
                      refining, and transportation. Only Category 3 (Fuel and Energy Related Activities) 
                      of the GHG Protocol Scope 3 standard is currently implemented.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Calculation Methodology</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground font-medium mb-1">Diesel Vehicles (Scope 1):</p>
                    <p className="text-muted-foreground font-mono bg-background px-3 py-2 rounded">
                      Emissions = Distance × Base Factor × Speed Correction × Gradient Correction
                    </p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Base Factor is interpolated based on laden percentage. Speed and gradient corrections 
                      are applied when telematics data is available (Primary Data).
                    </p>
                  </div>
                  
                  {serviceType === 'logistics' && (
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Electric Vehicles (Scope 2):</p>
                      <p className="text-muted-foreground font-mono bg-background px-3 py-2 rounded">
                        Energy = Distance × Base × Laden % × Speed Correction × Gradient Correction
                      </p>
                      <p className="text-muted-foreground font-mono bg-background px-3 py-2 rounded mt-2">
                        Emissions = Energy × Grid Emission Factor
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">
                        Energy consumption is calculated with laden percentage and correction factors, 
                        then converted to emissions using the UK grid emission factor.
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-muted-foreground font-medium mb-1">Idle Emissions:</p>
                    <p className="text-muted-foreground">
                      Idle emissions are calculated separately based on idle hours and burn rate 
                      (diesel) or consumption rate (electric), then added to active emissions.
                    </p>
                  </div>
                </div>
              </div>

              {serviceType === 'logistics' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Cost Data Sources</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Fuel Prices:</p>
                      <p className="text-muted-foreground">
                        <strong className="text-muted-foreground">Diesel:</strong> Weekly Road Fuel Prices (GOV.UK), Nov 2025 (145.40 p/litre)
                      </p>
                      <p className="text-muted-foreground">
                        <strong className="text-muted-foreground">Electricity:</strong> BEIS Quarterly Energy Prices, Q2 2025 (23.77 p/kWh)
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">
                        These prices are used for cost calculations only and are not emission factors.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Data Quality & Verification</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  <strong className="text-white">Primary Data:</strong> Emissions calculated using telematics data 
                  (GPS, speed, weight, altitude) with correction factors applied. This provides the most accurate 
                  and audit-ready results.
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed mt-2">
                  <strong className="text-white">Estimated Data:</strong> When telematics data is unavailable, 
                  emissions are calculated using fallback methods with default values. These estimates are clearly 
                  marked and may be less precise than primary data.
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed mt-2">
                  When data quality coverage is ≥ 80%, the report is marked as <strong className="text-success">Audit-Ready </strong> 
                  and <strong className="text-ring">CSRD Compliant</strong>, indicating it meets regulatory requirements 
                  for corporate sustainability reporting.
                </p>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* KPI Cards - 4 cards in a row, styled like ProfitabilityReport */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {(() => {
            const scope1Data = vehicleTypeLevelCSV.filter(row => !isElectricVehicle(row.Vehicle_Type || ''));
            const scope2Data = vehicleTypeLevelCSV.filter(row => isElectricVehicle(row.Vehicle_Type || ''));
            
            const scope1Emissions = scope1Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
            const scope2Emissions = scope2Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
            const totalEmissions = scope1Emissions + scope2Emissions;
            
            if (serviceType === 'logistics') {
              // Logistics mode: 4 cards in a row
              return (
                <>
                  {/* Total CO2e Emissions */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-3/20 via-card to-background rounded-xl border border-chart-3/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-3/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-3 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total CO2e Emissions</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {totalEmissions >= 1000 ? `${(totalEmissions / 1000).toFixed(1)}t` : `${totalEmissions.toFixed(1)}kg`}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Total fleet emissions</div>
                    </div>
                  </div>

                  {/* Fuel Cost */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-1/20 via-card to-background rounded-xl border border-chart-1/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-1/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-1 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Fuel Cost</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {getTotalFuelCost()}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Total fuel & electricity cost</div>
                    </div>
                  </div>

                  {/* Fuel Usage */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-2/20 via-card to-background rounded-xl border border-chart-2/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-2/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-2 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Fuel Usage</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {getTotalFuelUsage()}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Active fuel consumption</div>
                    </div>
                  </div>

                  {/* Energy Consumption */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-5/20 via-card to-background rounded-xl border border-chart-5/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-5/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-5 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Energy Consumption</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {getTotalEnergyConsumption()}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">EV electricity usage</div>
                    </div>
                  </div>
                </>
              );
            } else {
              // ES mode: 2 cards + pie chart
              return (
                <>
                  {/* Fuel Cost */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-1/20 via-card to-background rounded-xl border border-chart-1/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-1/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-1 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Fuel Cost</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {getTotalFuelCost()}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Total fuel cost</div>
                    </div>
                  </div>

                  {/* Fuel Usage */}
                  <div 
                    className="relative overflow-hidden bg-gradient-to-br from-chart-2/20 via-card to-background rounded-xl border border-chart-2/30 p-6 shadow-xl "
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-chart-2/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-chart-2 rounded-full animate-pulse"></div>
                        <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Fuel Usage</div>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {getTotalFuelUsage()}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Active fuel consumption</div>
                    </div>
                  </div>
                </>
              );
            }
          })()}
        </div>

        {/* Donut Chart and Top Emitting Vehicles */}
        {(() => {
          const scope1Data = vehicleTypeLevelCSV.filter(row => !isElectricVehicle(row.Vehicle_Type || ''));
          const scope2Data = vehicleTypeLevelCSV.filter(row => isElectricVehicle(row.Vehicle_Type || ''));
          
          const scope1Emissions = scope1Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
          const scope2Emissions = scope2Data.reduce((sum, row) => sum + getTotalEmissionsFromRow(row), 0);
          
          const pieChartData = [];
          
          if (scope1Emissions > 0) {
            pieChartData.push({
              name: 'Scope 1 (Diesel)',
              value: scope1Emissions,
              color: CHART_PALETTE[3]
            });
          }
          
          if (scope2Emissions > 0) {
            pieChartData.push({
              name: 'Scope 2 (Electric)',
              value: scope2Emissions,
              color: CHART_PALETTE[0]
            });
          }
          
          // Get top emitting vehicles from both scope 1 and 2
          const allVehicles = vehicleLevelCSV
            .map(row => {
              const vehicleDisplay = row.vehicle || row.Vehicle_ID || row.VehicleID || row.vehicle_id || 'Unknown';
              return {
                vehicleId: vehicleDisplay,
                vehicleType: row.VehicleType || row.Vehicle_Type || 'Unknown',
                emissions: getTotalEmissionsFromRow(row)
              };
            })
            .filter(v => v.emissions > 0)
            .sort((a, b) => b.emissions - a.emissions)
            .slice(0, 5);
          
          // Create color gradient from green (low emissions) to red (high emissions)
          const getEmissionColor = (value: number, min: number, max: number): string => {
            const normalized = max > min ? (value - min) / (max - min) : 0.5;
            // Green (#22c55e) to Red (#ef4444)
            const green = hexToRgb(STATUS_COLORS.success);
            const red = hexToRgb(STATUS_COLORS.destructive);
            
            const r = Math.round(green.r + (red.r - green.r) * normalized);
            const g = Math.round(green.g + (red.g - green.g) * normalized);
            const b = Math.round(green.b + (red.b - green.b) * normalized);
            
            return `rgb(${r}, ${g}, ${b})`;
          };
          
          const minEmissions = allVehicles.length > 0 ? allVehicles[allVehicles.length - 1].emissions : 0;
          const maxEmissions = allVehicles.length > 0 ? allVehicles[0].emissions : 1;
          
          
          if (pieChartData.length > 0) {
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Emissions by Scope Donut Chart */}
                <div className="bg-card rounded-lg shadow-sm p-6 border border-border flex flex-col" key={`pie-chart-${selectedPeriod}-${scope3Data?.summary?.total_scope3_emissions_kg || 0}`}>
                  <h3 className="text-lg font-semibold text-white pb-3">Emissions by Scope</h3>
                  <div className="flex-1 [&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0">
                    <DonutChart
                      data={pieChartData.map(item => ({
                        label: item.name,
                        value: item.value,
                        color: item.color
                      }))}
                      title=""
                    />
                  </div>
                </div>

                {/* Top Emitting Vehicles Bar Chart */}
                {allVehicles.length > 0 && (
                  <div className="bg-card rounded-lg shadow-sm p-6 border border-border flex flex-col">
                    <h3 className="text-lg font-semibold text-white pb-3">Top Emitting Vehicles</h3>
                    <div
                      className="flex-1 [&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0"
                      style={{ minHeight: `${Math.max(320, allVehicles.length * 52 + 80)}px` }}
                    >
                      <HorizontalBarChart
                        data={allVehicles.map((vehicle) => ({
                          label: vehicle.vehicleId,
                          values: [{
                            vehicle: vehicle.vehicleType,
                            value: vehicle.emissions,
                            color: getEmissionColor(vehicle.emissions, minEmissions, maxEmissions)
                          }]
                        }))}
                        title=""
                        xAxisLabel="CO2e (kg)"
                        yAxisLabel="Vehicle ID"
                        height="h-full"
                        showPercentage={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          }
          
          return null;
        })()}
      </div>

      <section className="mt-4 pt-8 overflow-visible w-full min-w-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible w-full min-w-0">
          {loading ? (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4"></div>
              <p className="text-muted-foreground">Loading carbon data...</p>
            </div>
          ) : timeSeriesData ? (
            <>
              {(() => {
                const dailyEmissionsSeries = getDailyEmissionsSeries();
                // Extract period from first date in data
                let periodText = '';
                if (dailyEmissionsSeries.length > 0 && dailyEmissionsSeries[0].data.length > 0) {
                  const firstDataPoint = dailyEmissionsSeries[0].data[0] as any;
                  if (firstDataPoint.date) {
                    const firstDate = new Date(firstDataPoint.date);
                    const monthName = firstDate.toLocaleDateString('en-GB', { month: 'short' });
                    const year = firstDate.getFullYear();
                    periodText = `${monthName} ${year}`;
                  }
                } else if (timeSeriesData?.emissions?.daily && timeSeriesData.emissions.daily.length > 0) {
                  const firstDate = new Date(timeSeriesData.emissions.daily[0].date);
                  const monthName = firstDate.toLocaleDateString('en-GB', { month: 'short' });
                  const year = firstDate.getFullYear();
                  periodText = `${monthName} ${year}`;
                } else if (selectedPeriod) {
                  periodText = selectedPeriod;
                }
                
                return dailyEmissionsSeries.length > 0 ? (
                <div>
                  <div className="mb-4 h-8"></div>
                  <LineChart
                      key={`emissions-${serviceType}-${selectedPeriod}`}
                      title={`Daily Carbon Emissions${periodText ? ` (${periodText})` : ''}`}
                      series={dailyEmissionsSeries}
                      yAxisLabel="CO2e (kg)"
                      xAxisLabel="Day of Month"
                      defaultZoomed
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Source: UK Government GHG Conversion Factors 2025
                  </p>
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
                  <p className="text-muted-foreground">No emissions data available for this period</p>
                </div>
                );
              })()}
              {(() => {
                const dailyFuelCostsSeries = getDailyFuelCostsSeries();
                // Extract period from first date in data
                let periodText = '';
                if (dailyFuelCostsSeries.length > 0 && dailyFuelCostsSeries[0].data.length > 0) {
                  // Try to get date from the first data point
                  const firstDataPoint = dailyFuelCostsSeries[0].data[0] as any;
                  if (firstDataPoint.date) {
                    const firstDate = new Date(firstDataPoint.date);
                    const monthName = firstDate.toLocaleDateString('en-GB', { month: 'short' });
                    const year = firstDate.getFullYear();
                    periodText = `${monthName} ${year}`;
                  }
                }
                // Fallback: try to get from timeSeriesData directly
                if (!periodText) {
                  if (timeSeriesData.fuel_costs?.daily && timeSeriesData.fuel_costs.daily.length > 0) {
                    const firstDate = new Date(timeSeriesData.fuel_costs.daily[0].date);
                    const monthName = firstDate.toLocaleDateString('en-GB', { month: 'short' });
                    const year = firstDate.getFullYear();
                    periodText = `${monthName} ${year}`;
                  } else if (timeSeriesData.diesel_fuel_costs?.daily && timeSeriesData.diesel_fuel_costs.daily.length > 0) {
                    const firstDate = new Date(timeSeriesData.diesel_fuel_costs.daily[0].date);
                    const monthName = firstDate.toLocaleDateString('en-GB', { month: 'short' });
                    const year = firstDate.getFullYear();
                    periodText = `${monthName} ${year}`;
                  } else if (selectedPeriod) {
                    periodText = selectedPeriod;
                  }
                }
                
                return dailyFuelCostsSeries.length > 0 ? (
                <div>
                  <div className="mb-4 h-8"></div>
                  <LineChart
                      key={`fuel-costs-${serviceType}-${selectedPeriod}`}
                      title={`Daily ${serviceType === 'emergency_services' ? 'Fuel Costs' : 'Fuel & Electricity Costs'}${periodText ? ` (${periodText})` : ''}`}
                      series={dailyFuelCostsSeries}
                      yAxisLabel="Fuel Cost (£)"
                      xAxisLabel="Day of Month"
                    defaultZoomed
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Sources: Weekly Road Fuel Prices (GOV.UK), Nov 2025 (145.40 p/litre) • BEIS Quarterly Energy Prices, Q2 2025 (23.77 p/kWh)
                  </p>
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
                  <p className="text-muted-foreground">No fuel costs data available for this period</p>
                </div>
                );
              })()}
            </>
          ) : (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
              <p className="text-muted-foreground">No carbon time-series data available</p>
            </div>
          )}
        </div>
      </section>

      {/* Data Quality & Verification Section */}
      <div className="bg-card rounded-lg shadow-sm p-4 border border-border mb-6 cursor-default">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Data Quality & Verification</h3>
          <div className="flex items-center gap-2">
            {qualityMetrics.coverage >= 80 && (
              <>
                <span className="px-3 py-1 bg-chart-3 text-white text-xs font-medium rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-chart-3/80 cursor-default">
                  ✅ Audit-Ready
                </span>
                <span className="px-3 py-1 bg-chart-1 text-white text-xs font-medium rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-chart-1/80 cursor-default flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  CSRD Compliant
                </span>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <span
              ref={primaryDataRef}
              className="px-2 py-1 bg-success text-white text-xs font-medium rounded cursor-help relative"
              onMouseEnter={() => {
                if (primaryDataRef.current) {
                  const rect = primaryDataRef.current.getBoundingClientRect();
                  setPrimaryDataTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top
                  });
                  setShowPrimaryDataTooltip(true);
                }
              }}
              onMouseLeave={() => {
                setShowPrimaryDataTooltip(false);
                setPrimaryDataTooltipPosition(null);
              }}
            >
              ✅ Primary Data
            </span>
            <span className="text-sm text-muted-foreground">{qualityMetrics.primaryData} vehicles</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              ref={estimatedRef}
              className="px-2 py-1 bg-warning text-white text-xs font-medium rounded cursor-help relative"
              onMouseEnter={() => {
                if (estimatedRef.current) {
                  const rect = estimatedRef.current.getBoundingClientRect();
                  setEstimatedTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top
                  });
                  setShowEstimatedTooltip(true);
                }
              }}
              onMouseLeave={() => {
                setShowEstimatedTooltip(false);
                setEstimatedTooltipPosition(null);
              }}
            >
              ⚠️ Estimated
            </span>
            <span className="text-sm text-muted-foreground">{qualityMetrics.estimatedData} vehicles</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Coverage:</span>
            <span className="text-sm font-medium text-white">{qualityMetrics.coverage}% primary</span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <div className="flex items-center gap-2">
              <span>Methodology: </span>
              <button
                onClick={() => setShowMethodologyModal(true)}
                className="text-warning hover:text-warning hover:underline flex items-center gap-1 transition-all"
              >
                GLEC Framework aligned
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Utilisation Metrics Section */}
      <section className="bg-card/50 rounded-lg p-6 border border-border">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Carbon Emissions</h2>
            <p className="text-muted-foreground text-sm">
              Monitor daily emissions and fuel costs. Track total impact and spending trends over time.
            </p>
          </div>
          {serviceType === 'emergency_services' && (
          <div className="flex gap-2">
            <select
              value={selectedVehicleType || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedVehicleType(value === '' ? null : value);
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                selectedVehicleType
                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                  : 'bg-secondary text-muted-foreground border-input hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
              style={{
                appearance: 'none',
                backgroundImage: selectedVehicleType 
                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                paddingRight: '2.5rem'
              }}
            >
              <option value="" className="bg-card text-muted-foreground">All Vehicle Types</option>
              {getVehicleTypes().map(vehicleType => (
                <option key={vehicleType} value={vehicleType} className="bg-card text-muted-foreground">
                  {redactVehicleType(vehicleType, isDemoMode)}
                </option>
              ))}
            </select>
          </div>
          )}
        </div>
        {/* Scope 1: Direct Emissions (Diesel Vehicles) */}
        {(() => {
          const scope1Data = getScope1VehicleData();
          // For ES mode, don't include aggregated data in Scope 1 (it goes in Vehicle Type Summary)
          // For Logistics mode, keep aggregated data in Scope 1
          const scope1Aggregated = serviceType === 'logistics' ? getScope1AggregatedData() : [];
          const combinedScope1Data = [...scope1Data, ...scope1Aggregated];
          if (combinedScope1Data.length === 0) return null;
          return (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Scope 1: Direct Emissions</h3>
                  <p className="text-sm text-muted-foreground">Diesel vehicles - Direct emissions from fuel combustion</p>
                </div>
            <button
              onClick={() => {
                    const columns = getScope1VehicleColumns();
                const headers = columns.map(col => col.label);
                const csv = [
                  headers.join(','),
                      ...combinedScope1Data.map(row => 
                    columns.map(col => {
                      if (col.key === 'vehicle_id') {
                        return (row as any)._vehicleId || '';
                      }
                      if (col.key === 'date') {
                        const v = (row as { date?: unknown }).date;
                        return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
                      }
                      const value = (row as any)[col.key];
                      return String(value || '').replace(/,/g, ';');
                    }).join(',')
                  )
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                    link.download = `scope1_vehicle_summary_${selectedVehicleType || 'all'}.csv`;
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
            >
              <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
              <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
            </button>
          </div>
          <DataTable
                key={`scope1-vehicle-summary-${selectedVehicleType || 'all'}`}
            title=""
                columns={getScope1VehicleColumns()}
                data={combinedScope1Data}
            hideExport
            customColumnWidths={{ 
              ...(serviceType === 'emergency_services' ? { date: { minWidth: 9, maxWidth: 12 } } : {}),
              vehicle_id: serviceType === 'logistics' 
                ? { minWidth: 18, maxWidth: 24 } 
                    : { minWidth: 8, maxWidth: 12 },
                  co2e_per_mile: { minWidth: 4, maxWidth: 14 }
            }}
          />
        </div>
          );
        })()}

        {/* Scope 2: Indirect Emissions (Electric Vehicles) */}
        {(() => {
          const scope2Data = getScope2VehicleData();
          const scope2Aggregated = getScope2AggregatedData();
          const combinedScope2Data = [...scope2Data, ...scope2Aggregated];
          if (combinedScope2Data.length === 0) return null;
          return (
            <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Scope 2: Indirect Emissions</h3>
                  <p className="text-sm text-muted-foreground">Electric vehicles - Indirect emissions from electricity consumption</p>
                </div>
                <button
                  onClick={() => {
                    const columns = getScope2VehicleColumns();
                    const headers = columns.map(col => col.label);
                    const csv = [
                      headers.join(','),
                      ...combinedScope2Data.map(row => 
                        columns.map(col => {
                          if (col.key === 'vehicle_id') {
                            return (row as any)._vehicleId || '';
                          }
                          if (col.key === 'date') {
                            const v = (row as { date?: unknown }).date;
                            return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
                          }
                          const value = (row as any)[col.key];
                          return String(value || '').replace(/,/g, ';');
                        }).join(',')
                      )
                    ].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `scope2_vehicle_summary_${selectedVehicleType || 'all'}.csv`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                >
                  <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                  <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                </button>
              </div>
              <DataTable
                key={`scope2-vehicle-summary-${selectedVehicleType || 'all'}`}
                title=""
                columns={getScope2VehicleColumns()}
                data={combinedScope2Data}
                hideExport
                customColumnWidths={{ 
                  ...(serviceType === 'emergency_services' ? { date: { minWidth: 9, maxWidth: 12 } } : {}),
                  vehicle_id: serviceType === 'logistics' 
                    ? { minWidth: 18, maxWidth: 24 } 
                    : { minWidth: 8, maxWidth: 12 },
                  co2e_per_mile: { minWidth: 4, maxWidth: 14 }
                }}
              />
            </div>
          );
        })()}

        {/* Vehicle Type Summary (Emergency Services only) */}
        {serviceType === 'emergency_services' && (() => {
          const aggregatedData = getScope1AggregatedData();
          if (aggregatedData.length === 0) return null;
          return (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
            <h3 className="text-lg font-semibold text-white">Vehicle Type Summary</h3>
                  <p className="text-sm text-muted-foreground">Aggregated emissions by vehicle type</p>
                </div>
            <button
              onClick={() => {
                const columns = getAggregatedVehicleColumns();
                const headers = columns.map(col => col.label);
                const csv = [
                  headers.join(','),
                      ...aggregatedData.map(row => 
                    columns.map(col => {
                      if (col.key === 'vehicle_type') {
                            return (row as any)._vehicleType || '';
                      }
                      if (col.key === 'date') {
                        const v = (row as { date?: unknown }).date;
                        return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
                      }
                      const value = (row as any)[col.key];
                      return String(value || '').replace(/,/g, ';');
                    }).join(',')
                  )
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `vehicle_type_summary_${selectedVehicleType || 'all'}.csv`;
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
            >
              <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
              <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
            </button>
          </div>
          <DataTable
                key={`vehicle-type-summary-${selectedVehicleType || 'all'}`}
            title=""
            columns={getAggregatedVehicleColumns()}
                data={aggregatedData}
            hideExport
                customColumnWidths={{ 
                  date: { minWidth: 9, maxWidth: 12 },
                  vehicle_type: { minWidth: 8, maxWidth: 16 },
                  co2e_per_mile: { minWidth: 4, maxWidth: 14 }
                }}
          />
        </div>
          );
        })()}
        
        {/* Station Summary (Emergency Services only) */}
        {serviceType === 'emergency_services' && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Station Summary</h3>
              <button
                onClick={() => {
                  const data = getStationSummaryData();
                  const columns = getStationSummaryColumns();
                  const headers = columns.map(col => col.label);
                  const csv = [
                    headers.join(','),
                    ...data.map(row => 
                      columns.map(col => {
                        if (col.key === 'station_name') {
                          return (row as any)._station_sortKey || '';
                        }
                        if (col.key === 'date') {
                          const v = (row as { date?: unknown }).date;
                          return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
                        }
                        const value = (row as any)[col.key];
                        return String(value || '').replace(/,/g, ';');
                      }).join(',')
                    )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `station_summary_${selectedVehicleType || 'all'}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
            </div>
            <DataTable
              key={`carbon-station-summary-${selectedVehicleType || 'all'}`}
              title=""
              columns={getStationSummaryColumns()}
              data={getStationSummaryData()}
              hideExport
              customColumnWidths={{ 
                date: { minWidth: 9, maxWidth: 12 },
                co2e_per_mile: { minWidth: 4, maxWidth: 14 }
              }}
            />
          </div>
        )}

        {/* Scope 3: Upstream Emissions (Logistics only) */}
        {serviceType === 'logistics' && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Scope 3: Well-to-Tank Emissions (Partial)</h3>
                <p className="text-sm text-muted-foreground">Upstream fuel production, refining, and transportation emissions. <span className="text-warning font-semibold">Note: This represents only Category 3 (Fuel and Energy Related Activities) of the GHG Protocol Scope 3 standard. A complete Scope 3 inventory would include additional categories such as purchased goods/services, transportation/distribution, waste, business travel, and downstream activities.</span></p>
              </div>
            </div>
            
            {scope3Loading ? (
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-40 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-warning mb-3"></div>
                <p className="text-muted-foreground">Loading Scope 3 emissions data...</p>
              </div>
            ) : scope3Data ? (
              <>
                {/* Summary KPI Card */}
                <div className="mb-6">
                  <div className="bg-card rounded-lg shadow-sm p-4 border border-border">
                    <div className={`grid gap-4 ${scope3Data.summary.total_distance_miles ? 'grid-cols-4' : 'grid-cols-3'}`}>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Total Scope 3 Emissions</div>
                        <div className="text-2xl font-bold text-white">
                          {scope3Data.summary.total_scope3_emissions_kg >= 1000 
                            ? `${(scope3Data.summary.total_scope3_emissions_kg / 1000).toFixed(1)}t`
                            : `${scope3Data.summary.total_scope3_emissions_kg.toFixed(1)}kg`}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Total Orders</div>
                        <div className="text-2xl font-bold text-white">{scope3Data.summary.total_orders.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Avg per Order</div>
                        <div className="text-2xl font-bold text-white">
                          {scope3Data.summary.avg_scope3_per_order_kg.toFixed(2)} kg CO₂e
                        </div>
                      </div>
                      {scope3Data.summary.total_distance_miles && (
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Total Distance</div>
                          <div className="text-2xl font-bold text-white">
                            {scope3Data.summary.total_distance_miles >= 1000
                              ? `${(scope3Data.summary.total_distance_miles / 1000).toFixed(1)}K mi`
                              : `${scope3Data.summary.total_distance_miles.toFixed(1)} mi`}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* By Transport Service */}
                {scope3Data.by_transport_service.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-white">By Transport Service</h4>
                      <button
                        onClick={() => {
                          const hasDistance = scope3Data.by_transport_service[0]?.total_distance_miles !== undefined;
                          const headers = hasDistance
                            ? ['Transport Service', 'kg CO₂e', 'kg CO₂e/mile', 'Order Count', 'Avg per Order (kg CO₂e)', 'Total Distance (mi)']
                            : ['Transport Service', 'kg CO₂e', 'Order Count', 'Avg per Order (kg CO₂e)'];
                          const csv = [
                            headers.join(','),
                            ...scope3Data.by_transport_service.map(row => {
                              const baseRow = [
                                redactTransportService(row.transport_service, isDemoMode),
                                row.total_scope3_emissions_kg.toFixed(2),
                              ];
                              if (hasDistance && row.total_distance_miles !== undefined) {
                                baseRow.push(
                                  row.co2e_per_mile !== undefined ? row.co2e_per_mile.toFixed(2) : '0.00'
                                );
                              }
                              baseRow.push(
                                row.order_count.toString(),
                                row.avg_scope3_per_order_kg.toFixed(2)
                              );
                              if (hasDistance && row.total_distance_miles !== undefined) {
                                baseRow.push(row.total_distance_miles.toFixed(2));
                              }
                              return baseRow.join(',');
                            })
                          ].join('\n');
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `scope3_by_transport_service_${selectedPeriod || 'all'}.csv`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                      >
                        <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                        <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                      </button>
                    </div>
                    <DataTable
                      key={`scope3-transport-service-${selectedPeriod}`}
                      title=""
                      columns={[
                        { key: 'transport_service', label: 'Transport Service' },
                        { key: 'total_scope3_emissions_kg', label: <CO2eLabel customTooltipText="Total Scope 3 upstream emissions from fuel production, refining, and transportation (Well-to-Tank). These are indirect emissions that occur before the fuel reaches the vehicle, including extraction, processing, and distribution of fuels." />, align: 'right' as const },
                        ...(scope3Data.by_transport_service[0]?.total_distance_miles ? [
                          { key: 'co2e_per_mile', label: 'CO₂e (kg/mile)', align: 'right' as const },
                        ] : []),
                        { key: 'order_count', label: 'Order Count', align: 'right' as const },
                        { key: 'avg_scope3_per_order_kg', label: 'Avg per Order (kg CO₂e)', align: 'right' as const },
                        ...(scope3Data.by_transport_service[0]?.total_distance_miles ? [
                          { key: 'total_distance_miles', label: 'Total Distance (mi)', align: 'right' as const },
                        ] : []),
                      ]}
                      data={(() => {
                        // Calculate all CO2e per mile values for normalization
                        const allCo2ePerMileValues = scope3Data.by_transport_service
                          .filter(row => row.co2e_per_mile !== undefined && row.co2e_per_mile > 0)
                          .map(row => row.co2e_per_mile!);
                        
                        // Map regular rows
                        const regularRows = scope3Data.by_transport_service.map(row => ({
                          transport_service: redactTransportService(row.transport_service, isDemoMode),
                          total_scope3_emissions_kg: formatEmission(row.total_scope3_emissions_kg.toString()),
                          total_scope3_emissions_kg_sortKey: row.total_scope3_emissions_kg,
                          ...(row.total_distance_miles !== undefined && row.co2e_per_mile !== undefined ? {
                            co2e_per_mile: formatNumber(row.co2e_per_mile, 2),
                            co2e_per_mile_sortKey: row.co2e_per_mile,
                            _co2ePerMileValue: row.co2e_per_mile > 0 ? getCo2ePerMileScore(row.co2e_per_mile, allCo2ePerMileValues) : 0,
                            _co2ePerMileColor: row.co2e_per_mile > 0 ? getScoreColor(getCo2ePerMileScore(row.co2e_per_mile, allCo2ePerMileValues)) : getScoreColor(0),
                          } : {}),
                          order_count: formatCount(row.order_count.toString()),
                          order_count_sortKey: row.order_count,
                          avg_scope3_per_order_kg: formatNumber(row.avg_scope3_per_order_kg, 2),
                          avg_scope3_per_order_kg_sortKey: row.avg_scope3_per_order_kg,
                          ...(row.total_distance_miles !== undefined ? {
                            total_distance_miles: formatMiles(row.total_distance_miles.toString()),
                            total_distance_miles_sortKey: row.total_distance_miles,
                          } : {}),
                        }));

                        // Calculate totals
                        const hasDistance = scope3Data.by_transport_service[0]?.total_distance_miles !== undefined;
                        const totals = scope3Data.by_transport_service.reduce((acc, row) => {
                          acc.total_scope3_emissions_kg += row.total_scope3_emissions_kg;
                          acc.order_count += row.order_count;
                          if (hasDistance && row.total_distance_miles !== undefined) {
                            acc.total_distance_miles += row.total_distance_miles;
                          }
                          return acc;
                        }, {
                          total_scope3_emissions_kg: 0,
                          order_count: 0,
                          total_distance_miles: 0,
                        });

                        // Calculate weighted average for co2e_per_mile (total emissions / total distance)
                        const totalCo2ePerMile = hasDistance && totals.total_distance_miles > 0
                          ? totals.total_scope3_emissions_kg / totals.total_distance_miles
                          : undefined;

                        // Calculate average per order (total emissions / total orders)
                        const avgPerOrder = totals.order_count > 0
                          ? totals.total_scope3_emissions_kg / totals.order_count
                          : 0;

                        // Create TOTAL row
                        const totalRow: any = {
                          transport_service: 'TOTAL',
                          total_scope3_emissions_kg: formatEmission(totals.total_scope3_emissions_kg.toString()),
                          total_scope3_emissions_kg_sortKey: totals.total_scope3_emissions_kg,
                          order_count: formatCount(totals.order_count.toString()),
                          order_count_sortKey: totals.order_count,
                          avg_scope3_per_order_kg: formatNumber(avgPerOrder, 2),
                          avg_scope3_per_order_kg_sortKey: avgPerOrder,
                          _isStickyRow: true, // Make this row sticky at the bottom
                        };

                        // Add distance-related fields if available
                        if (hasDistance && totals.total_distance_miles !== undefined) {
                          totalRow.total_distance_miles = formatMiles(totals.total_distance_miles.toString());
                          totalRow.total_distance_miles_sortKey = totals.total_distance_miles;
                          if (totalCo2ePerMile !== undefined) {
                            totalRow.co2e_per_mile = formatNumber(totalCo2ePerMile, 2);
                            totalRow.co2e_per_mile_sortKey = totalCo2ePerMile;
                            // Calculate progress bar values for TOTAL row
                            if (totalCo2ePerMile > 0 && allCo2ePerMileValues.length > 0) {
                              totalRow._co2ePerMileValue = getCo2ePerMileScore(totalCo2ePerMile, allCo2ePerMileValues);
                              totalRow._co2ePerMileColor = getScoreColor(getCo2ePerMileScore(totalCo2ePerMile, allCo2ePerMileValues));
                            } else {
                              totalRow._co2ePerMileValue = 0;
                              totalRow._co2ePerMileColor = getScoreColor(0);
                            }
                          }
                        }

                        return [...regularRows, totalRow];
                      })()}
                      hideExport
                      customColumnWidths={{ 
                        transport_service: { minWidth: 12, maxWidth: 20 },
                        total_scope3_emissions_kg: { minWidth: 6, maxWidth: 12 },
                        ...(scope3Data.by_transport_service[0]?.total_distance_miles ? {
                          co2e_per_mile: { minWidth: 6, maxWidth: 14 },
                        } : {}),
                        order_count: { minWidth: 4, maxWidth: 8 },
                        avg_scope3_per_order_kg: { minWidth: 6, maxWidth: 12 },
                        ...(scope3Data.by_transport_service[0]?.total_distance_miles ? {
                          total_distance_miles: { minWidth: 6, maxWidth: 12 },
                        } : {}),
                      }}
                    />
                  </div>
                )}

                {/* By Origin-Destination Pairs */}
                {scope3Data.by_origin_destination_pair.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-white">By Origin-Destination Pairs</h4>
                      <button
                        onClick={() => {
                          const hasDistance = scope3Data.by_origin_destination_pair[0]?.total_distance_miles !== undefined;
                          const headers = hasDistance
                            ? ['Origin City', 'Destination City', 'kg CO₂e', 'kg CO₂e/mile', 'Order Count', 'Avg per Order (kg CO₂e)', 'Total Distance (mi)']
                            : ['Origin City', 'Destination City', 'kg CO₂e', 'Order Count', 'Avg per Order (kg CO₂e)'];
                          const csv = [
                            headers.join(','),
                            ...scope3Data.by_origin_destination_pair.map(row => {
                              const baseRow = [
                                redactCityName(row.origin_city, isDemoMode),
                                redactCityName(row.destination_city, isDemoMode),
                                row.total_scope3_emissions_kg.toFixed(2),
                              ];
                              if (hasDistance && row.total_distance_miles !== undefined) {
                                baseRow.push(
                                  row.co2e_per_mile !== undefined ? row.co2e_per_mile.toFixed(2) : '0.00'
                                );
                              }
                              baseRow.push(
                                row.order_count.toString(),
                                row.avg_scope3_per_order_kg.toFixed(2)
                              );
                              if (hasDistance && row.total_distance_miles !== undefined) {
                                baseRow.push(row.total_distance_miles.toFixed(2));
                              }
                              return baseRow.join(',');
                            })
                          ].join('\n');
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `scope3_by_origin_destination_${selectedPeriod || 'all'}.csv`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                      >
                        <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                        <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                      </button>
                    </div>
                    <DataTable
                      key={`scope3-origin-destination-${selectedPeriod}`}
                      title=""
                      columns={[
                        { key: 'origin_city', label: 'Origin City' },
                        { key: 'destination_city', label: 'Destination City' },
                        { key: 'total_scope3_emissions_kg', label: <CO2eLabel customTooltipText="Total Scope 3 upstream emissions from fuel production, refining, and transportation (Well-to-Tank). These are indirect emissions that occur before the fuel reaches the vehicle, including extraction, processing, and distribution of fuels." />, align: 'right' as const },
                        ...(scope3Data.by_origin_destination_pair[0]?.total_distance_miles ? [
                          { key: 'co2e_per_mile', label: 'CO₂e (kg/mile)', align: 'right' as const },
                        ] : []),
                        { key: 'order_count', label: 'Order Count', align: 'right' as const },
                        { key: 'avg_scope3_per_order_kg', label: 'Avg per Order (kg CO₂e)', align: 'right' as const },
                        ...(scope3Data.by_origin_destination_pair[0]?.total_distance_miles ? [
                          { key: 'total_distance_miles', label: 'Total Distance (mi)', align: 'right' as const },
                        ] : []),
                      ]}
                      data={(() => {
                        // Calculate all CO2e per mile values for normalization
                        const allCo2ePerMileValues = scope3Data.by_origin_destination_pair
                          .filter(row => row.co2e_per_mile !== undefined && row.co2e_per_mile > 0)
                          .map(row => row.co2e_per_mile!);
                        
                        return scope3Data.by_origin_destination_pair.map(row => ({
                          origin_city: redactCityName(row.origin_city, isDemoMode),
                          destination_city: redactCityName(row.destination_city, isDemoMode),
                          total_scope3_emissions_kg: formatEmission(row.total_scope3_emissions_kg.toString()),
                          total_scope3_emissions_kg_sortKey: row.total_scope3_emissions_kg,
                          ...(row.total_distance_miles !== undefined && row.co2e_per_mile !== undefined ? {
                            co2e_per_mile: formatNumber(row.co2e_per_mile, 2),
                            co2e_per_mile_sortKey: row.co2e_per_mile,
                            _co2ePerMileValue: row.co2e_per_mile > 0 ? getCo2ePerMileScore(row.co2e_per_mile, allCo2ePerMileValues) : 0,
                            _co2ePerMileColor: row.co2e_per_mile > 0 ? getScoreColor(getCo2ePerMileScore(row.co2e_per_mile, allCo2ePerMileValues)) : getScoreColor(0),
                          } : {}),
                          order_count: formatCount(row.order_count.toString()),
                          order_count_sortKey: row.order_count,
                          avg_scope3_per_order_kg: formatNumber(row.avg_scope3_per_order_kg, 2),
                          avg_scope3_per_order_kg_sortKey: row.avg_scope3_per_order_kg,
                          ...(row.total_distance_miles !== undefined ? {
                            total_distance_miles: formatMiles(row.total_distance_miles.toString()),
                            total_distance_miles_sortKey: row.total_distance_miles,
                          } : {}),
                        }));
                      })()}
                      hideExport
                      customColumnWidths={{ 
                        origin_city: { minWidth: 10, maxWidth: 16 },
                        destination_city: { minWidth: 10, maxWidth: 16 },
                        total_scope3_emissions_kg: { minWidth: 6, maxWidth: 12 },
                        ...(scope3Data.by_origin_destination_pair[0]?.total_distance_miles ? {
                          co2e_per_mile: { minWidth: 6, maxWidth: 14 },
                        } : {}),
                        order_count: { minWidth: 4, maxWidth: 8 },
                        avg_scope3_per_order_kg: { minWidth: 6, maxWidth: 12 },
                        ...(scope3Data.by_origin_destination_pair[0]?.total_distance_miles ? {
                          total_distance_miles: { minWidth: 6, maxWidth: 12 },
                        } : {}),
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-40 flex items-center justify-center">
                <p className="text-muted-foreground">No Scope 3 emissions data available for this period</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Manual Fuel/Energy Entry Modal */}
      {showManualCarbonForm && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => setShowManualCarbonForm(false)}
        >
          <div
            className="bg-card rounded-lg shadow-xl border border-border max-w-4xl w-full max-h-screen-90 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="w-5 h-5 text-success" />
                <div>
                  <h2 className="text-xl font-bold text-white">Log Fuel or Energy</h2>
                  <p className="text-sm text-muted-foreground">Manually enter fuel consumption or electricity usage for vehicles without telematics</p>
                </div>
              </div>
              <button
                onClick={() => setShowManualCarbonForm(false)}
                className="text-muted-foreground hover:text-white transition-colors text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleCarbonSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Row 1: Date, Time, Vehicle ID, Vehicle Type */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Date <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={manualCarbonForm.date}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, date: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Time <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="time"
                    required
                    value={manualCarbonForm.time}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, time: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                  />
                </div>

                <div className="md:col-span-1">
                  <div className="flex items-center justify-between mb-2 relative">
                    <label className="text-sm font-medium text-white">
                      Vehicle ID <span className="text-destructive">*</span>
                    </label>
                    <div className="absolute right-0" style={{ width: '28px', height: '28px' }}>
                      {manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' && (
                        <button
                          type="button"
                          onClick={() => {
                            setManualCarbonForm({ ...manualCarbonForm, vehicle_id: '', vehicle_type: '' });
                            setNewVehicleIdCarbon('');
                            setNewVehicleTypeCarbon('');
                            setShowNewVehicleTypeInputCarbon(false);
                          }}
                          className="px-2 py-1 bg-accent text-white rounded-md border border-accent hover:bg-muted-foreground transition-all"
                          title="Go back to selecting existing vehicles"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' ? (
                    <input
                      type="text"
                      required
                      value={newVehicleIdCarbon}
                      onChange={(e) => setNewVehicleIdCarbon(e.target.value)}
                      className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                      placeholder="Enter vehicle ID"
                    />
                  ) : (
                    <div className="relative">
                      {manualCarbonForm.vehicle_id && manualCarbonForm.vehicle_id !== '' ? (
                        <input
                          type="text"
                          readOnly
                          value={getSelectedVehicleDisplayCarbon()}
                          className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input pointer-events-none"
                        />
                      ) : null}
                      <select
                        required
                        value={manualCarbonForm.vehicle_id}
                        onChange={(e) => handleVehicleSelectCarbon(e.target.value)}
                        className={`w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all cursor-pointer ${manualCarbonForm.vehicle_id && manualCarbonForm.vehicle_id !== '' ? 'absolute inset-0 opacity-0' : ''}`}
                        style={manualCarbonForm.vehicle_id && manualCarbonForm.vehicle_id !== '' ? { zIndex: 10 } : {}}
                      >
                        <option value="">Select a vehicle</option>
                        {getAvailableVehicles().map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {redactVehicleId(vehicle.id, isDemoMode)} {vehicle.type ? `(${redactVehicleType(vehicle.type, isDemoMode)})` : ''}
                          </option>
                        ))}
                        <option value="__NEW_VEHICLE__">+ Add a new vehicle</option>
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Vehicle Type
                  </label>
                  {manualCarbonForm.vehicle_id === '__NEW_VEHICLE__' ? (
                    <>
                      {!showNewVehicleTypeInputCarbon ? (
                        <select
                          value={manualCarbonForm.vehicle_type}
                          onChange={(e) => {
                            if (e.target.value === '__NEW_VEHICLE_TYPE__') {
                              setShowNewVehicleTypeInputCarbon(true);
                              setManualCarbonForm({ ...manualCarbonForm, vehicle_type: '' });
                            } else {
                              setManualCarbonForm({ ...manualCarbonForm, vehicle_type: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all cursor-pointer"
                        >
                          <option value="">Select vehicle type</option>
                          {getVehicleTypes().map(vehicleType => (
                            <option key={vehicleType} value={vehicleType}>
                              {redactVehicleType(vehicleType, isDemoMode)}
                            </option>
                          ))}
                          <option value="__NEW_VEHICLE_TYPE__">+ Add a new vehicle type</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={newVehicleTypeCarbon}
                          onChange={(e) => {
                            setNewVehicleTypeCarbon(e.target.value);
                            setManualCarbonForm({ ...manualCarbonForm, vehicle_type: e.target.value });
                          }}
                          className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                          placeholder="Enter new vehicle type"
                        />
                      )}
                    </>
                  ) : (
                    <select
                      value={manualCarbonForm.vehicle_type}
                      disabled
                      className="w-full px-3 py-2 bg-card text-muted-foreground rounded-md border border-input cursor-not-allowed"
                    >
                      <option value={manualCarbonForm.vehicle_type}>
                        {manualCarbonForm.vehicle_type ? redactVehicleType(manualCarbonForm.vehicle_type, isDemoMode) : 'N/A'}
                      </option>
                    </select>
                  )}
                </div>

                {/* Row 2: Fuel Type, Quantity, Cost, Distance */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Fuel Type <span className="text-destructive">*</span>
                  </label>
                  <select
                    required
                    value={manualCarbonForm.fuel_type}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, fuel_type: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all cursor-pointer"
                  >
                    <option value="Diesel">Diesel</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Electricity">Electricity</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Quantity <span className="text-destructive">*</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({manualCarbonForm.fuel_type === 'Electricity' ? 'kWh' : 'Liters'})
                    </span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.1"
                    value={manualCarbonForm.quantity}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Cost (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualCarbonForm.cost}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, cost: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Distance (miles)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={manualCarbonForm.odometer}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, odometer: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                    placeholder="Optional"
                  />
                </div>

                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-white mb-2">
                    Notes
                  </label>
                  <textarea
                    value={manualCarbonForm.notes}
                    onChange={(e) => setManualCarbonForm({ ...manualCarbonForm, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-secondary text-white rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                    rows={2}
                    placeholder="Optional notes about this fuel/energy entry"
                  />
                </div>
              </div>

              {carbonSubmitSuccess && (
                <div className="flex items-center gap-2 p-3 bg-success/20 border border-success/50 rounded-md text-success">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Fuel/energy entry logged successfully. Your entry has been added to the table below.</span>
                </div>
              )}

              {carbonSubmitError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/20 border border-destructive/50 rounded-md text-destructive">
                  <XCircle className="w-5 h-5" />
                  <span>{carbonSubmitError}</span>
                </div>
              )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowManualCarbonForm(false)}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingCarbon}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {submittingCarbon ? 'Submitting...' : 'Log Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Manual Carbon Entries Table */}
      <section className="bg-card rounded-lg shadow-sm border border-border">
        <div className="border-t border-border p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-white">Manual Fuel/Energy Entries</h4>
            <div className="flex items-center gap-3">
              {selectedCarbonEntries.size > 0 && (
                <button
                  onClick={handleDeleteSelectedCarbon}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-destructive rounded-md hover:bg-destructive hover:border-destructive hover:shadow-lg hover:shadow-destructive/50 transition-all border border-transparent group"
                  title={`Delete ${selectedCarbonEntries.size} selected ${selectedCarbonEntries.size === 1 ? 'entry' : 'entries'}`}
                >
                  <Trash2 className="w-4 h-4 group-hover:text-white group-hover:drop-shadow-glow-critical-lg group-hover:scale-150 transition-all duration-300" />
                  <span className="group-hover:text-white group-hover:drop-shadow-glow-critical-md transition-all">Delete ({selectedCarbonEntries.size})</span>
                </button>
              )}
              <button
                onClick={() => setShowManualCarbonForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Plus className="w-4 h-4 group-hover:text-white group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-white group-hover:drop-shadow-glow-success-md transition-all">Fuel</span>
              </button>
              <button
                onClick={() => {
                  // Export manual carbon entries to CSV
                  const headers = ['Date', 'Time', 'Vehicle ID', 'Vehicle Type', 'Fuel Type', 'Quantity', 'Cost (£)', 'Odometer', 'Notes', 'Logged At'];
                  const csv = [
                    headers.join(','),
                    ...manualCarbonEntries.map(entry => [
                      entry.date || '',
                      entry.time || '',
                      entry.vehicle_id || '',
                      entry.vehicle_type || '',
                      entry.fuel_type || '',
                      entry.quantity || '0.0',
                      entry.cost || '0.00',
                      entry.odometer || '',
                      (entry.notes || '').replace(/"/g, '""'),
                      entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : ''
                    ].map(val => `"${val}"`).join(','))
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `manual_carbon_entries_${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
            </div>
          </div>
          {manualCarbonEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-card border-b border-border">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground w-12">
                      <input
                        type="checkbox"
                        checked={selectedCarbonEntries.size === manualCarbonEntries.length && manualCarbonEntries.length > 0}
                        onChange={(e) => handleSelectAllCarbon(e.target.checked)}
                        className="w-4 h-4 rounded border-input bg-secondary text-success focus:ring-2 focus:ring-success"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Datetime</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Vehicle ID</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Vehicle Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Fuel Type</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-muted-foreground">Quantity</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-muted-foreground">Cost (£)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Distance (miles)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Notes</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Logged At</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {manualCarbonEntries.map((entry, idx) => {
                    // Combine date and time, format like "Logged At" column
                    const dateTimeStr = entry.date && entry.time 
                      ? `${entry.date} ${entry.time}`
                      : entry.date || entry.time || '';
                    const dateTime = dateTimeStr ? new Date(dateTimeStr) : null;
                    const formattedDateTime = dateTime && !isNaN(dateTime.getTime())
                      ? dateTime.toLocaleString('en-GB')
                      : dateTimeStr || '-';
                    
                    return (
                      <tr
                        key={idx}
                        className="hover:bg-accent/40 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedCarbonEntries.has(idx)}
                            onChange={() => handleCheckboxChangeCarbon(idx)}
                            className="w-4 h-4 rounded border-input bg-secondary text-success focus:ring-2 focus:ring-success"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-white">{formattedDateTime}</td>
                        <td className="px-4 py-3 text-sm text-white">{redactVehicleId(entry.vehicle_id || '', isDemoMode)}</td>
                        <td className="px-4 py-3 text-sm text-white">{redactVehicleType(entry.vehicle_type || '', isDemoMode)}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.fuel_type || ''}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{entry.quantity ? `${parseFloat(entry.quantity).toFixed(1)} ${entry.fuel_type === 'Electricity' ? 'kWh' : 'L'}` : '0.0'}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{entry.cost ? `£${parseFloat(entry.cost).toFixed(2)}` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.odometer || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.notes || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No manual fuel/energy entries yet. Click the "+ Fuel" button to log an entry.</p>
            </div>
          )}
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      {deleteCarbonConfirm && deleteCarbonConfirm.length > 0 && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteCarbonConfirm(null)}>
          <div className="bg-card border border-input rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Deletion</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to remove {deleteCarbonConfirm.length} selected {deleteCarbonConfirm.length === 1 ? 'entry' : 'entries'}?
              <br />
              <span className="text-sm text-muted-foreground mt-2 block">
                This action cannot be undone.
              </span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteCarbonConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCarbonEntries}
                className="px-4 py-2 text-sm font-medium text-white bg-destructive rounded-md hover:bg-destructive transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
        </>
      )}

      {/* Client Carbon Reporting Section */}
      {activeSection === 'client' && (
        <ClientCarbonReporting />
      )}
    </div>
  );
}